/**
 * GlobalChatSystem Styled Components
 * 
 * This file contains all styled components for the global chat system.
 * These components can be used across any page that implements the chat.
 */

import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import { mediaMax } from '../../styles/breakpoints';
import { CHAT_LAYOUT } from './types';
import { HEADER_RAIL_WIDE_MIN_PX } from './headerRailTiers';
import {
  CHAT_FULLSCREEN_Z_INDEX,
  CHAT_DOCKED_Z_INDEX,
  CHAT_DOCKED_COLLAPSED_Z_INDEX,
  CHAT_FLOATING_Z_INDEX,
  CHAT_PROMPT_BAR_Z_INDEX,
  CHAT_LAUNCHER_Z_INDEX,
  BODY_PORTAL_Z_INDEX,
} from '../../styles/layers';

// ============================================================================
// Animations
// ============================================================================

export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeInCentered = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

export const fadeInDocked = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Mode-settle crossfade (dock-glide round, 2026-08-24): the floating↔docked
 * switch tweens right/width/transform, but the pane's HEIGHT jumps (content-
 * driven when floating, full rail when docked — auto↔length can't animate).
 * A brief opacity settle masks that one snap so the whole move reads as a
 * single gesture. Two identical keyframe sets on purpose: swapping the
 * animation NAME is what restarts the animation exactly when the mode flips
 * (the same trick PromptBarContainer plays with fadeInDocked/fadeInCentered).
 */
export const modeSettleDocked = keyframes`
  from { opacity: 0.55; }
  to { opacity: 1; }
`;

export const modeSettleFloating = keyframes`
  from { opacity: 0.55; }
  to { opacity: 1; }
`;

export const messageFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const streamingPulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

// Streaming indicator styled component
export const StreamingIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
  animation: ${streamingPulse} 1.5s ease-in-out infinite;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--brand-gold);
    animation: ${streamingPulse} 1.5s ease-in-out infinite;
  }
`;

// Global CSS for streaming pulse animation (for inline HTML usage)
export const STREAMING_PULSE_CSS = `
  @keyframes streamingPulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`;

// Global CSS for processing tooltip animation (for portal-rendered tooltip)
export const PROCESSING_TOOLTIP_CSS = `
  @keyframes processingTooltipFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, 10px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
  @keyframes processingSpinnerRotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ============================================================================
// Pending Widget Components (Modern, sleek processing indicator)
// ============================================================================

// Spinning animation for the pending widget
export const pendingSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Shimmer effect for the pending widget background
export const pendingShimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Subtle glow pulse for the pending widget
export const pendingGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 12px rgba(198, 167, 94, 0.2), 0 4px 16px rgba(198, 167, 94, 0.1);
  }
  50% {
    box-shadow: 0 0 20px rgba(198, 167, 94, 0.35), 0 6px 24px rgba(198, 167, 94, 0.2);
  }
`;

// Dot bounce animation for loading dots
export const dotBounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
`;

/**
 * PendingWidget - A sleek, modern processing indicator for long-running AI operations
 * Use this as a standalone component or via the PENDING_WIDGET_HTML for inline rendering.
 */
export const PendingWidget = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  background: linear-gradient(135deg, var(--brand-canvas) 0%, #f5f0e4 50%, var(--brand-canvas) 100%);
  background-size: 200% 100%;
  animation: ${pendingShimmer} 3s ease-in-out infinite, ${pendingGlow} 2s ease-in-out infinite;
  border-radius: 24px;
  border: 1px solid rgba(198, 167, 94, 0.15);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
  /* Gold as TEXT on the canvas gradient measures 2.79:1 against 4.5
     (theme.ts, 2026-09-12). This is the "thinking" widget, so it is only ever
     on screen while a turn is in flight — which is also why axe never caught
     it: the scan happens at rest, and at rest this element does not exist. */
  color: var(--brand-gold-text);
  letter-spacing: 0.01em;
  user-select: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(198, 167, 94, 0.25);
  }
`;

/**
 * Spinner ring for the pending widget
 */
export const PendingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(198, 167, 94, 0.2);
  border-top-color: var(--brand-gold);
  border-radius: 50%;
  animation: ${pendingSpin} 0.8s linear infinite;
  flex-shrink: 0;
`;

/**
 * Loading dots container
 */
export const PendingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 2px;
`;

/**
 * Individual loading dot
 */
export const PendingDot = styled.span<{ $delay?: number }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--brand-gold);
  animation: ${dotBounce} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.$delay || 0}s;
`;

/**
 * Status text inside the pending widget
 */
export const PendingText = styled.span`
  color: var(--brand-gold-hover);
  font-weight: 500;
`;

/**
 * Stop button - small black square that appears on hover (React component version)
 * Position this after the text in flex container, not inside spinner container
 * Uses display:none by default so bubble size is unchanged until hover
 */
export const PendingStopButton = styled.button<{ $visible?: boolean }>`
  width: 8px;
  height: 8px;
  min-width: 8px;
  min-height: 8px;
  max-width: 8px;
  max-height: 8px;
  border: none;
  border-radius: 1px;
  background: #000;
  cursor: pointer;
  display: ${props => props.$visible ? 'inline-flex' : 'none'};
  margin-left: 6px;
  flex-shrink: 0;
  transition: background 0.15s ease;
  padding: 0;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  line-height: 0;

  &:hover {
    background: #333;
  }
`;

/**
 * Container for spinner (React component version)
 * Note: Stop button should be placed after text, not inside this container
 */
export const PendingSpinnerContainer = styled.div`
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

/**
 * Global CSS for pending widget (injected via createGlobalStyle)
 * These keyframes are needed for the inline HTML widget to animate properly.
 */
export const PENDING_WIDGET_CSS = `
  @keyframes pendingSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes pendingShimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  @keyframes pendingGlow {
    0%, 100% {
      box-shadow: 0 0 12px rgba(198, 167, 94, 0.2), 0 4px 16px rgba(198, 167, 94, 0.1);
    }
    50% {
      box-shadow: 0 0 20px rgba(198, 167, 94, 0.35), 0 6px 24px rgba(198, 167, 94, 0.2);
    }
  }
  @keyframes dotBounce {
    0%, 80%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    40% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }

  /* Pending widget hover interactions for stop button */
  .pending-widget-wrapper:hover .pending-stop-btn {
    display: inline-flex !important;
  }
  .pending-stop-btn {
    width: 8px !important;
    height: 8px !important;
    min-width: 8px !important;
    min-height: 8px !important;
    max-width: 8px !important;
    max-height: 8px !important;
  }
  .pending-stop-btn:hover {
    background: #333 !important;
  }

  /* Parallel branches (PLAN-prest0n-parallel-branches-20260908): the rail of
     per-branch rows under the pill. Class rules carry what the chat
     sanitizer's inline allow-list cannot (ellipsis, narrow-pane collapse,
     reduced motion); the row skeleton itself is inline (branchLane.ts). */
  .pending-widget-stack { max-width: 100%; }
  .pending-branch-rail { max-width: 100%; }
  .pending-branch-row { max-width: 100%; }
  .pending-branch-label { overflow: hidden; text-overflow: ellipsis; }
  .pending-branch-clock { font-variant-numeric: tabular-nums; }
  .pending-branch-row.is-done .pending-branch-state,
  .pending-branch-row.is-cancelled .pending-branch-state { opacity: 0.65 !important; }
  @media (max-width: 700px) {
    .pending-branch-state { display: none !important; }
  }
  @container chat-pane (max-width: 520px) {
    .pending-branch-state { display: none !important; }
  }
  @media (prefers-reduced-motion: reduce) {
    .pending-branch-glyph.is-spin { animation-duration: 2s !important; }
    .pending-widget-wrapper { animation: none !important; }
  }
`;

/**
 * Global styles for pending widget animations.
 * Must be rendered once in GlobalChatSystem to enable inline HTML widget animations.
 */
export const PendingWidgetGlobalStyles = createGlobalStyle`
  ${PENDING_WIDGET_CSS}
`;

/**
 * Overlay container for pending widget (renders outside chat bubbles)
 * Positioned absolutely at bottom-left of chat area so it remains visible when scrolling
 */
export const PendingOverlayContainer = styled.div`
  position: static;
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-start;
  pointer-events: none;
  /* Never wider than the pane row (docked-mode UX, 2026-07-13): the old
     white-space: nowrap / max-width: none combination let long status labels
     ("research is still running — this will update automatically") overflow
     the 420px docked pane, clipping the pill's right end INCLUDING the
     hover-revealed stop button — the only cancel affordance on this row.
     The pill's text wraps instead; the inline-flex wrapper tolerates
     multi-line spans. */
  min-width: 0;
  max-width: 100%;

  & > div {
    pointer-events: all;
    max-width: 100%;
    min-width: 0;
  }
`;

/**
 * Full-width host row for the tool-owned pending widget (phantom-chip fix,
 * Miles 2026-07-13). The widget used to render inside the bottom action rail
 * as a flex SIBLING of the thread chips, where its pill shape read as a
 * phantom third thread. It now owns its own in-flow row between the messages
 * area and the thread rail: full width, left-aligned, matching the pane
 * background — unmistakably a status, never a chip.
 */
export const PendingOverlayRow = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-start;
  padding: 2px 16px 6px 16px;
  background: var(--brand-canvas); /* matches ChatMessages background */
  /* The lane can NEVER crush the transcript (2026-08-22): with no height
     bound inside the overflow:hidden height-capped pane, one oversized
     pending-shaped message collapsed ChatMessages (flex:1, shrinkable to 0)
     to a sliver. The extraction gate now keeps content-bearing streams
     inline, but no future widget may squeeze the conversation either. */
  max-height: min(30vh, 240px);
  overflow-y: auto;

  @media (max-width: 700px) {
    padding: 2px 10px 6px 10px;
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    padding: 2px 10px 6px 10px;
  }
`;

// NOTE: getPendingWidgetHtml and getStoppedWidgetHtml have been moved to
// src/services/globalTools/widgetHtml.ts to avoid circular dependencies.
// Import directly from that file instead of from here.

// ============================================================================
// Chat Container Components
// ============================================================================

/**
 * Chat layering — the numbers live in src/styles/layers.ts, once, for the
 * whole app (2026-09-11). These re-exports keep every existing import site
 * working; the contract, the history and the rule for picking a rung are in
 * that file.
 *
 * The one rule worth repeating where chat code can see it: anything rendered
 * via createPortal into document.body competes with the chat at the ROOT
 * stacking context, so a portal below the pane opens invisibly BEHIND it.
 * Every body-portaled overlay uses BODY_PORTAL_Z_INDEX and wears
 * data-prest0n-portal="true" — never a bespoke number, never an unmarked
 * root. scripts/check-overlay-layers.mjs fails the build on either.
 */
export {
  CHAT_FULLSCREEN_Z_INDEX,
  CHAT_DOCKED_Z_INDEX,
  CHAT_FLOATING_Z_INDEX,
  BODY_PORTAL_Z_INDEX,
};

export const ChatContainer = styled.div<{ $isOpen: boolean; $isDocked: boolean; $isFullScreen?: boolean; $zIndex?: number }>`
  position: fixed;
  ${props => props.$isFullScreen ? `
    /* Inset below the pinned banner band (2026-08-31 consistency directive):
       the band pins itself at the very top of the page while this takeover is
       up, and publishes its measured height into this variable. Unset (no
       band registered) resolves to 0px, i.e. the original full-bleed top. */
    top: var(--prest0n-banner-band-height, 0px);
    /* left: auto, not 0 (dock-glide round, 2026-08-24): width + right now
       TRANSITION, and with left:0 + right:0 + width all set, CSS resolves
       left+width and ignores right — so mid-transition the pane teleported
       to the LEFT edge and grew rightward. Anchoring right (left auto)
       keeps the fullscreen takeover expanding leftward out of the pane's
       own right-anchored geometry; the final frame is identical (right: 0,
       width: 100% spans the viewport). */
    left: auto;
    right: 0;
    /* The on-screen keyboard, as an ordinary height change (2026-09-14).
       Unset — every desktop browser, and Android, where the LAYOUT viewport
       already shrank — resolves to 0px, which is byte-for-byte the previous
       bottom:0 declaration. On iOS Safari, which does not implement
       interactive-widget, this is the only thing that tells the pane the
       keyboard is there. Shrinking the pane is what makes the transcript's
       clientHeight move, which is what makes the scroll controller's
       ResizeObserver fire and re-stick a pinned viewport — so the keyboard
       needs no special case anywhere else. See keyboardInset.ts. */
    bottom: var(--prest0n-keyboard-inset, 0px);
    width: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    transform: none;
    /* T3 (thread-workflow UX): make room for the fullscreen thread sidebar.
       The sidebar hides itself <=820px, where this padding also resets. */
    @media (min-width: 821px) {
      padding-left: 264px;
      /* Rail gutter (header placement round, Miles 2026-07-14): the 264px
         above stops EXACTLY at the sidebar's right boundary (the rail is
         also 264px wide), so the content area is structurally FLUSH against
         the rail and ChatHeader's own left inset was the only separation —
         the identity block read as glued to the sidebar edge. ChatHeader
         consumes this variable as ADDITIONAL left inset (see its padding
         rules), giving the avatar one full inset step of clear air off the
         rail. Every other mode leaves it unset (0px fallback); it resets
         together with the padding <=820px where the rail hides. */
      --chat-rail-gap: 1.5rem;
    }
  ` : `
    bottom: ${props.$isDocked ? `${CHAT_LAYOUT.DOCKED_BOTTOM}px` : CHAT_LAYOUT.BOTTOM_OFFSET};
    ${props.$isDocked
      /* Dock side (owner directive 2026-08-20): the docked pane anchors
         RIGHT, always — there is no left branch, by design. The siblings
         hardwire the same side (DockedCollapsedBar, PromptBarContainer, the
         closed pill, justiceisurright's DockedContentWrapper), so a left
         pane would split the UI. Convention lives at CHAT_LAYOUT.DOCK_SIDE;
         do not reintroduce a side prop here.
         Geometry note (dock-glide round, 2026-08-24): the pane's full
         height is expressed as bottom + height (was top + bottom) — the
         same 96px top edge — so BOTH modes anchor on `right`/`bottom` and
         the floating↔docked switch tweens right/width/transform instead of
         snapping between incompatible anchor sets. The floating branch
         centers via right: 50% + translateX(50%) for the same reason. */
      ? `height: calc(100vh - ${CHAT_LAYOUT.DOCKED_TOP + CHAT_LAYOUT.DOCKED_BOTTOM}px); height: calc(100dvh - ${CHAT_LAYOUT.DOCKED_TOP + CHAT_LAYOUT.DOCKED_BOTTOM}px); right: 24px; left: auto;`
      : 'right: 50%; left: auto;'}
    transform: ${props.$isDocked ? 'translateX(0)' : `translateX(50%) ${props.$isOpen ? 'scale(1)' : 'scale(0.98)'}`};
    width: ${props.$isDocked ? CHAT_LAYOUT.DOCKED_WIDTH : CHAT_LAYOUT.WIDTH};
    max-height: ${props.$isOpen ? (props.$isDocked ? `calc(100vh - ${CHAT_LAYOUT.DOCKED_TOP + CHAT_LAYOUT.DOCKED_BOTTOM}px)` : 'min(80vh, 720px)') : '0'};
    /* dvh re-declaration (mobile UX 2026-08-14): vh ignores mobile browser
       chrome, so 80vh panes ran under the URL bar. Older browsers drop the
       dvh line and keep the vh one above. */
    max-height: ${props.$isOpen ? (props.$isDocked ? `calc(100dvh - ${CHAT_LAYOUT.DOCKED_TOP + CHAT_LAYOUT.DOCKED_BOTTOM}px)` : 'min(80dvh, 720px)') : '0'};
    border-radius: 16px;
  `}
  background: var(--brand-canvas);
  box-shadow: ${props => props.$isOpen ? '0 18px 50px rgba(0,0,0,0.18)' : '0 8px 30px rgba(0,0,0,0.12)'};
  overflow: hidden;
  /* Pane-based sizing (docked-mode UX round, Miles 2026-07-13): the pane is a
     CSS container so compact layout INSIDE the chat keys off the pane's own
     width via "@container chat-pane (...)" — not the viewport. Viewport media
     queries never fired for the fixed 420px docked pane on desktop, which is
     why docked mode got wide-pane padding squeezed into 420px. Viewport
     queries below remain only for ChatContainer's OWN geometry. (inline-size
     containment is safe here: every branch sets an explicit width, and the
     existing transform/backdrop-filter already made this the containing
     block for positioned descendants.) */
  container-type: inline-size;
  container-name: chat-pane;
  /* Dock glide (2026-08-24): right + width joined the list so the
     floating↔docked switch slides instead of snapping (both modes anchor on
     "right" now — see the positioning branch above). transform keeps the
     house spring so the move lands with the signature settle; right/width
     ride the smoother header curve so pane content never jiggles through an
     overshoot; border-radius covers the fullscreen 16px↔0 morph. */
  transition: max-height 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              right 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              border-radius 0.4s cubic-bezier(0.2, 0.8, 0.2, 1),
              opacity 0.3s ease,
              visibility 0.3s ease;
  /* Mode-settle crossfade — open, non-fullscreen only: a mode flip swaps
     the animation NAME (docked↔floating variant), restarting the fade that
     masks the un-animatable height snap. Closed panes keep animation:none
     so the opacity transition owns the fade-out, and a dock flip while
     closed can never flash an invisible pane. */
  animation: ${props => (!props.$isOpen || props.$isFullScreen)
    ? 'none'
    : css`${props.$isDocked ? modeSettleDocked : modeSettleFloating} 0.35s ease`};
  /* Docked = page chrome below the header layer so top-nav menus overlap
     the pane (see CHAT_DOCKED_Z_INDEX); floating/config overrides keep the
     legacy overlay layer. */
  z-index: ${props => props.$isFullScreen
    ? CHAT_FULLSCREEN_Z_INDEX
    : (props.$isDocked ? CHAT_DOCKED_Z_INDEX : (props.$zIndex ?? CHAT_FLOATING_Z_INDEX))};
  display: flex;
  flex-direction: column;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  /* visibility, not just opacity+pointer-events (review 2026-08-14): the
     fullscreen branch has no closed max-height clip, and descendants like
     ThreadNavBubble re-enable pointer-events — an invisible full-viewport
     closed pane was stealing taps at the top of the page on every phone
     (fullscreen is sticky-true on mobile). visibility:hidden disables
     hit-testing for the whole subtree; the transition keeps the fade-out. */
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  border: 1px solid var(--brand-line);
  backdrop-filter: blur(10px);
  /* 2026-09-08 (smooth playback): while a clip plays inline, the pane's backdrop blur is lifted —
     re-blurring the backdrop behind a 30 fps video on every frame is the one cost a playing
     <video> cannot absorb on a modest GPU. The inline player sets the attribute on <html>. */
  html[data-prest0n-media-playing] & {
    backdrop-filter: none;
  }
  transform-origin: bottom center;

  @media (max-width: 900px) {
    ${props => !props.$isFullScreen && `
      width: 95%;
      ${props.$isDocked ? 'height: calc(100vh - 100px); height: calc(100dvh - 100px); bottom: 20px; max-height: calc(100vh - 100px); max-height: calc(100dvh - 100px);' : ''}
    `}
  }

  @media (max-width: 600px) {
    ${props => !props.$isFullScreen && `
      width: calc(100% - 2rem);
      ${props.$isDocked ? 'height: calc(100vh - 88px); height: calc(100dvh - 88px); bottom: 16px; max-height: calc(100vh - 88px); max-height: calc(100dvh - 88px);' : ''}
    `}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
  }
`;

export const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  /* Vertical centering contract (header placement round, Miles 2026-07-14):
     the bar is the single vertical reference — the identity block and the
     action cluster BOTH center against it right here, in every mode. No bar
     child may carry vertical margins (HeaderTitleGroup / HeaderActions pin
     an explicit margin: 0), so nothing nudges a line off the bar's axis. */
  align-items: center;
  /* Minimum separation that survives even when the title block maxes out —
     without it, space-between collapses to 0 and the truncated title sits
     flush against the first action button (docked-mode UX, 2026-07-13). */
  gap: 12px;
  /* One horizontal inset rhythm across floating/docked/fullscreen (header
     placement round, Miles 2026-07-14): the bar's inset is declared ONCE in
     --chat-header-inset — wide panes 1.5rem; the <=480px compact tier below
     (docked round, kept) only re-tunes the variable. --chat-rail-gap is the
     fullscreen-only gutter ChatContainer sets at >=821px (its padding-left
     stops exactly at the thread rail's boundary, so without the gutter the
     identity block sat flush against the rail); everywhere else it falls
     back to 0px and left == right inset. */
  --chat-header-inset: 1.5rem;
  padding: 1rem var(--chat-header-inset);
  padding-left: calc(var(--chat-header-inset) + var(--chat-rail-gap, 0px));
  background: linear-gradient(to right, var(--brand-canvas), #ffffff);
  border-bottom: 1px solid var(--brand-line);
  border-radius: 16px 16px 0 0;
  flex-shrink: 0;

  /* Narrow pane (docked 420px, small phones): trim horizontal padding so the
     title column keeps usable width next to the fixed action cluster. */
  @container chat-pane (max-width: 520px) {
    --chat-header-inset: 1.25rem;
  }
`;

/**
 * Header layout contract (docked-mode UX round, Miles 2026-07-13):
 * the ACTION BUTTONS are invariant; the TITLE is what shrinks.
 *
 * - HeaderTitleGroup (logo + title column) is the flexible block: it absorbs
 *   all shortfall via `flex: 1 1 auto; min-width: 0` so the thread label's
 *   ellipsis engages at the real available width.
 * - HeaderTitleColumn continues the min-width:0 chain down to the label /
 *   rename input (flexbox min-width:auto would otherwise make the column
 *   non-compressible and push the buttons past the pane edge).
 * - HeaderActions never shrinks (`flex: 0 0 auto`) so the trash / mode-cycle /
 *   hide buttons stay fully visible and clickable at every pane width.
 */
export const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  /* No stray margins (header placement round, 2026-07-14): the bar's
     align-items: center is the only vertical authority — an explicit 0
     keeps the identity block on the bar's axis in every mode. */
  margin: 0;
`;

export const HeaderTitleColumn = styled.div`
  min-width: 0;
  overflow: hidden;
  /* Identity block (header polish, Miles 2026-07-13): the classic chat-header
     arrangement — a tight two-line column beside the invariant logo. Line 1 is
     the status dot + persona name (ChatTitle), line 2 the gold thread label /
     subtitle. The column owns the 2px line gap and both lines share the SAME
     left edge; the old per-line margin-top / 12px padding-left offsets made
     the two lines sit visibly misaligned against the avatar. Fonts, sizes and
     colors are unchanged — arrangement only. */
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  /* No stray margins (header placement round, 2026-07-14): same rule as
     HeaderTitleGroup — the bar centers the cluster; nothing offsets it. */
  margin: 0;
`;

export const ChatHeaderLogoImage = styled.img`
  width: 36px;
  height: 36px;
  /* Like the action buttons, the logo is invariant — only the title text
     shrinks (header priority rule, docked-mode UX 2026-07-13). */
  flex-shrink: 0;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(198, 167, 94, 0.15);
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.08), rgba(198, 167, 94, 0.04));
`;

export const ChatTitle = styled.div<{ $accentColor?: string }>`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-text);
  display: flex;
  align-items: center;
  gap: 0;
  /* A long headerTitleElement clips inside the title column instead of
     pushing the header's action buttons off the pane edge. */
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;

  /* Phone widths (owner mobile report 2026-08-12): "Connect my Goo…" told the
     user nothing about which thread they were in. A phone header has room for
     TWO lines — wrap instead of guillotining at one, bounded by max-height so
     a runaway title still can never push the action rail off the pane edge.
     Stays display:flex (an -webkit-box clamp would drop the ::before accent
     dot onto its own line); align-items:flex-start keeps the dot on line 1.
     Dropping the docked rung on mobile (useGlobalChat) also returns one
     button's worth of width to this column. */
  @media (max-width: 640px) {
    white-space: normal;
    align-items: flex-start;
    line-height: 1.25;
    max-height: 2.5em; /* exactly two lines */
    word-break: break-word;
  }

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.$accentColor || 'var(--brand-gold)'};
    margin-right: 4px;
    /* Flex item: never let a long wrapped title squeeze the dot oval. */
    flex-shrink: 0;
  }

  /* Two-line mobile title (see the media block above): nudge the dot down so
     it reads as a bullet on line 1 rather than floating above the cap line. */
  @media (max-width: 640px) {
    &::before {
      margin-top: 0.34em;
    }
  }
`;

export const HeaderSubtitle = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--brand-text-secondary);
  letter-spacing: 0.03em;
  text-transform: lowercase;
  /* Line offsets removed (header identity block, 2026-07-13):
     HeaderTitleColumn now owns the line gap, and the subtitle shares the
     column's left edge with the persona name above it. */

  /* The PEOPLE LINE never breaks inside its phrase (header rail tiers,
     2026-09-15). Miles's foldable-landscape screenshot showed "6" over
     "people" under an eight-button rail: the row was nowrap but this item
     was not, so flex shrink split the count from its noun. Scoped to the
     participants line because the same component renders a white-label
     surface's configured subtitle (GlobalChatSystem's no-thread header),
     which sits inside HeaderTitleColumn's overflow:hidden with no ellipsis
     and must keep wrapping. */
  &[data-participants-line] {
    white-space: nowrap;
  }
`;

/**
 * Row wrapping the participants line and the summon-mode pill (2026-08-07).
 * min-width: 0 lets the pill ellipsize under docked header pressure instead of
 * pushing the title rail.
 */
export const HeaderSubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  /* Phones (2026-09-13). Owner report on the fullscreen header: "where the
     fort shows the number of people in an awkward position, everything should
     be nice and neat. and square it up." The people line and the summon pill
     shared one nowrap row inside HeaderTitleColumn's overflow:hidden, so at
     390px the pill was cut mid-word by the clip rather than wrapped — the
     "awkward position". Wrapping is the square-up: two tidy lines instead of
     one severed one, and the pill's own phone copy (see GlobalChatSystem)
     usually means it does not have to. */
  ${mediaMax.mobileViewport} {
    flex-wrap: wrap;
    row-gap: 3px;
  }

  /* Header rail tiers (2026-09-15): the same square-up wherever the PANE is
     narrower than the wide rail needs — a foldable in landscape (fullscreen
     chat beside the 264px thread sidebar), the floating pane on a small
     laptop — keyed on the pane's own width through the container query, the
     SAME number the JS tier rule reads (headerRailTiers.HEADER_RAIL_WIDE_MIN_PX),
     so the rail and this row can never disagree about which width is
     "narrow". Wide panes stay on one line, exactly as before. */
  @container chat-pane (max-width: ${HEADER_RAIL_WIDE_MIN_PX - 1}px) {
    flex-wrap: wrap;
    row-gap: 3px;
  }
`;

/**
 * Summon-mode pill — names the thread's @Prest0n mode in words, in both
 * states (owner feedback 2026-08-07: the icon-only toggle was not "intuitive
 * and clear ... when this is on or off").
 *
 * States, via data-summon-pill:
 *   'required' → gold accent (#C6A75E vocabulary shared with the header
 *                chrome): a mention is required, Prest0n is deliberately quiet
 *                otherwise.
 *   'open'     → neutral: Prest0n replies to every message.
 *   'error'    → a setting write was rejected; the pill carries the notice
 *                text so a failed save is never silent.
 */
export const HeaderSummonPill = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  line-height: 1;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f5f5f5;
  color: var(--brand-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 0 1 auto;

  &[data-summon-pill='required'] {
    background: rgba(198, 167, 94, 0.12);
    border-color: var(--brand-gold);
    color: #8a6d2f;
  }

  &[data-summon-pill='error'] {
    background: rgba(176, 32, 32, 0.08);
    border-color: rgba(176, 32, 32, 0.45);
    color: #b02020;
  }
`;

export const ChatMessages = styled.div<{ $hasFloatingButtons?: boolean }>`
  padding: 1.5rem;
  padding-top: ${props => props.$hasFloatingButtons ? '3.5rem' : '1.5rem'};
  /* Bottom reserve (2026-08 scroll redesign): the last message / follow-up
     chip row must never sit flush against the sticky footer. */
  padding-bottom: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--brand-canvas);

  /* Thread scrolling stays INSIDE the thread (2026-08-12 scrolling round):
     hitting the transcript's top/bottom must never hand the wheel/touch
     momentum to the page behind the pane — that chained page-scroll is what
     made the chat "walk away" mid-read in floating/docked modes. Fullscreen
     additionally locks the page itself (useBodyScrollLock). */
  overscroll-behavior: contain;

  /* Prominent thread scrollbar (2026-08-12 scrolling round): the old 6px
     hairline was nearly invisible, so the thread didn't LOOK scrollable and
     the grab target was fiddly. A wider rail with an always-visible padded
     thumb keeps the affordance obvious in EVERY mode (floating / docked /
     fullscreen — this component is the scroller for all three). */
  &::-webkit-scrollbar {
    width: 14px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(90, 82, 60, 0.35);
    border-radius: 8px;
    /* Transparent border + padding-box clip = a fat 14px hit area with a
       slimmer visible bar that still reads clearly against #FAFAF8. */
    border: 3px solid transparent;
    background-clip: padding-box;
    min-height: 48px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(90, 82, 60, 0.55);
  }

  &::-webkit-scrollbar-thumb:active {
    background-color: rgba(90, 82, 60, 0.7);
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
  }

  /* Firefox: full-size (not thin) scrollbar, matching thumb/track tint. */
  scrollbar-width: auto;
  scrollbar-color: rgba(90, 82, 60, 0.45) rgba(0, 0, 0, 0.05);

  /* NO CSS smooth scrolling on this container (2026-08 scroll redesign): it
     turns every corrective scrollTop write into an animation — the "dragged
     to the bottom" feel. Smoothness is explicit JS behavior via
     scrollMotion.ts, which also honors prefers-reduced-motion. */

  @media (max-width: 720px) {
    padding: 1rem;
    padding-top: ${props => props.$hasFloatingButtons ? '3rem' : '1rem'};
    padding-bottom: 1.75rem;
  }

  @media (max-width: 600px) {
    padding: 0.75rem;
    padding-top: ${props => props.$hasFloatingButtons ? '2.75rem' : '0.75rem'};
    padding-bottom: 1.5rem;
  }

  /* Narrow PANE (desktop docked 420px included) — the viewport queries above
     never fire there; keyed off the chat-pane container instead. Values
     mirror the tightest viewport tier so mobile behavior is unchanged. */
  @container chat-pane (max-width: 520px) {
    padding: 0.75rem;
    padding-top: ${props => props.$hasFloatingButtons ? '2.75rem' : '0.75rem'};
    padding-bottom: 1.5rem;
  }
`;

/**
 * Inner wrapper around EVERYTHING rendered in the transcript (messages,
 * dividers, follow-up chips). Owns the inter-item flex gap that used to live
 * on ChatMessages, and gives useTranscriptScroll's ResizeObserver a single
 * content box to watch: growth that changes no message identity — chips
 * resolving after the reply, images decoding, copy-button decoration — still
 * re-sticks a pinned viewport, which is what keeps suggested replies fully
 * visible at the bottom of the thread.
 */
export const TranscriptContent = styled.div`
  display: flex;
  flex-direction: column;

  /* ── Speaker-aware vertical rhythm (2026-08 reading-comfort round) ──
     Owner report: "messages are crowding each other." The transcript used one
     uniform gap for every row, so four consecutive lines from Prest0n and a
     hand-off between speakers looked identical — a wall of text rather than a
     conversation.

     The flex gap below is the TIGHT, same-speaker value: consecutive rows from
     one sender sit close, reading as a single continued utterance. A row that
     opens a new turn (data-turn-start, stamped by the render from
     messageTurns.startsNewTurn) adds margin ON TOP of that gap, so a change of
     speaker gets visibly more air. The turn rule is a DESCENDANT selector, not
     a child one, so it reaches rows inside the reveal-older wrapper too. */
  gap: 0.5rem;

  [data-turn-start='true']:not(:first-child) {
    /* 8px gap + 12px = 20px between speakers */
    margin-top: 0.75rem;
  }

  /* The reveal-older wrapper is a plain block, so revealed history used to
     stack with NO spacing at all — the worst crowding in the thread. Give it
     the same flex rhythm as the live tail. */
  .reveal-inner {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @media (max-width: 720px) {
    gap: 0.45rem;

    .reveal-inner {
      gap: 0.45rem;
    }

    [data-turn-start='true']:not(:first-child) {
      margin-top: 0.6rem;
    }
  }

  @media (max-width: 600px) {
    gap: 0.4rem;

    .reveal-inner {
      gap: 0.4rem;
    }

    [data-turn-start='true']:not(:first-child) {
      margin-top: 0.55rem;
    }
  }

  /* Narrow pane (desktop docked included) — mirrors the tightest tier. */
  @container chat-pane (max-width: 520px) {
    gap: 0.4rem;

    .reveal-inner {
      gap: 0.4rem;
    }

    [data-turn-start='true']:not(:first-child) {
      margin-top: 0.55rem;
    }
  }
`;

// Subtle pulse animation for new message indicator
const newMessagePulse = keyframes`
  0%, 100% {
    box-shadow: 0 2px 8px rgba(198, 167, 94, 0.2), 0 0 0 0 rgba(198, 167, 94, 0.3);
  }
  50% {
    box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3), 0 0 0 4px rgba(198, 167, 94, 0.1);
  }
`;

export const ScrollToBottomButton = styled.button<{ $visible: boolean; $hasNewMessages?: boolean }>`
  position: absolute;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%) ${props => props.$visible ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  background: ${props => props.$hasNewMessages ? 'linear-gradient(135deg, var(--brand-gold), var(--brand-gold-hover))' : 'rgba(255, 255, 255, 0.95)'};
  border: 1px solid ${props => props.$hasNewMessages ? 'transparent' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 20px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: ${props => props.$hasNewMessages ? 600 : 500};
  color: ${props => props.$hasNewMessages ? '#ffffff' : '#64748b'};
  box-shadow: ${props => props.$hasNewMessages
    ? '0 4px 14px rgba(198, 167, 94, 0.35)'
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  backdrop-filter: blur(8px);
  z-index: 10;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  ${props => props.$hasNewMessages && css`
    animation: ${newMessagePulse} 2s ease-in-out infinite;
  `}

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  &:hover {
    background: ${props => props.$hasNewMessages ? 'linear-gradient(135deg, var(--brand-gold-hover), #a07e3a)' : '#ffffff'};
    color: ${props => props.$hasNewMessages ? '#ffffff' : '#0f172a'};
    box-shadow: ${props => props.$hasNewMessages
      ? '0 6px 20px rgba(198, 167, 94, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.15)'};
    transform: translateX(-50%) translateY(2px);
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }

  @media (max-width: 600px) {
    padding: 5px 10px;
    font-size: 0.7rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    padding: 5px 10px;
    font-size: 0.7rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

/**
 * Container for floating navigation buttons (Show Older + Latest).
 * Positioned absolutely at top of scroll area, uses flexbox for side-by-side layout.
 */
export const FloatingButtonBar = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;
  pointer-events: none; /* Allow clicks to pass through container */

  & > * {
    pointer-events: auto; /* But buttons themselves are clickable */
  }
`;

/**
 * The BOTTOM dock, for the arrival pill (2026-09-13).
 *
 * FloatingButtonBar above is anchored to the top of the transcript, which is
 * right for the two things it holds: "previous" and the ↑/↓ steppers both take
 * you UP. The "N new" pill was in it too, and on touch the steppers are hidden
 * — so the arrival signal rendered as a single orphaned chip floating over the
 * reading line while the messages it referred to were a screen and a half
 * below. That is the "latest ⌄" chip in the owner's screenshot.
 *
 * A signal about the bottom belongs at the bottom. ChatMessages' own
 * `padding-bottom: 2rem` means it sits over whitespace rather than text.
 */
export const FloatingPillDock = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 10;
  pointer-events: none; /* Allow clicks to pass through container */

  & > * {
    pointer-events: auto; /* But buttons themselves are clickable */
  }
`;

/**
 * Generic floating pill button - shared style for both Show Older and Latest buttons.
 *
 * `$dock` says which edge it hides toward, so a bottom-docked pill slides down
 * out of view instead of up through the transcript it is sitting on.
 */
export const FloatingPillButton = styled.button<{
  $visible: boolean;
  $variant?: 'primary' | 'secondary';
  $dock?: 'top' | 'bottom';
}>`
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transform: ${props => (props.$visible
    ? 'translateY(0)'
    : `translateY(${props.$dock === 'bottom' ? '100%' : '-100%'})`)};
  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, var(--brand-gold), var(--brand-gold-hover))'
    : 'rgba(255, 255, 255, 0.95)'};
  border: 1px solid ${props => props.$variant === 'primary' ? 'transparent' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 20px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: ${props => props.$variant === 'primary' ? 600 : 500};
  /* Charcoal on gold, never white (theme.ts, 2026-09-12: gold is a FILL).
     The primary pill paints on a --brand-gold -> --brand-gold-hover
     gradient, and white against the darker stop measures 2.9:1 — under
     AA on the one control that exists to be noticed. Charcoal is 4.9:1
     against that same stop. check-design-tokens.mjs checks hex literals
     and var() existence, so it could not see this. */
  color: ${props => props.$variant === 'primary' ? 'var(--brand-charcoal)' : '#64748b'};
  box-shadow: ${props => props.$variant === 'primary'
    ? '0 4px 14px rgba(198, 167, 94, 0.35)'
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  backdrop-filter: blur(8px);
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  &:hover {
    background: ${props => props.$variant === 'primary'
      ? 'linear-gradient(135deg, var(--brand-gold-hover), #a07e3a)'
      : '#ffffff'};
    color: ${props => props.$variant === 'primary' ? 'var(--brand-charcoal)' : '#0f172a'};
    box-shadow: ${props => props.$variant === 'primary'
      ? '0 6px 20px rgba(198, 167, 94, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.15)'};
    transform: translateY(2px);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 600px) {
    padding: 5px 10px;
    font-size: 0.7rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    padding: 5px 10px;
    font-size: 0.7rem;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

/**
 * Round message-step bubble (2026-08-12 scrolling round): compact chevron
 * bubbles that walk the thread ONE MESSAGE at a time — ↑ to the previous
 * message, ↓ toward the latest. They sit in FloatingButtonBar beside the
 * reveal-older / jump-to-latest pills, share that family's visual language,
 * and stay visible in every mode (floating / docked / fullscreen) whenever
 * the thread has messages to walk, so navigation never depends on hunting
 * the scrollbar. A bubble at its edge (top-most / bottom-most) renders
 * dimmed via :disabled instead of vanishing, keeping the cluster stable.
 */
export const ThreadNavBubble = styled.button<{ $visible: boolean }>`
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transform: ${props => props.$visible ? 'translateY(0)' : 'translateY(-100%)'};
  /* 2026-08 reading-comfort round: the blanket touch hide moved OFF this
     button and onto the top-center bar that carried it (FloatingNavPair).
     The 2026-08-14 complaint was positional — pagers parked over the message
     text at the top of a phone screen — not "no stepping on phones". Touch
     now gets the same controls in ThreadNavCluster: thumb-side, larger than
     the desktop bubble, and asleep until you scroll. */
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  &:hover:not(:disabled) {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(2px);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: ${props => props.$visible ? 0.35 : 0};
    cursor: default;
    pointer-events: none;
  }

  @media (max-width: 600px) {
    width: 30px;
    height: 30px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    width: 30px;
    height: 30px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

/**
 * The top-center ↑/↓ pair, desktop only. Touch keeps this position clear:
 * parking pagers over the message text at the top of a phone screen is the
 * exact 2026-08-14 complaint that got them removed. Phones use
 * ThreadNavCluster instead.
 */
export const FloatingNavPair = styled.div`
  /* A real flex row rather than display:contents — the parent bar turns
     pointer-events off and re-enables it per child, and a box-less element
     makes that (and the 8px rhythm) subtly harder to reason about. Same 8px
     gap in and out, so the desktop bar is pixel-identical. */
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;

  @media (hover: none) {
    display: none;
  }
`;

/**
 * Touch-only message stepper (2026-08 reading-comfort round). Owner directive:
 * message-by-message navigation on phones too, one model everywhere.
 *
 * Three things keep it from repeating the overlay complaint that killed the
 * first attempt:
 *   • it sits on the RIGHT EDGE, vertically centred — never in the top-center
 *     reading line, and clear of the thread strip and composer in every mode;
 *   • it SLEEPS: invisible and click-through until you scroll or step, then
 *     fades out again, so a still thread is never covered;
 *   • it only mounts when the thread is actually long enough to step through.
 * Buttons are 44px — the accessible touch minimum, vs the 34px desktop bubble.
 */
export const ThreadNavCluster = styled.div<{ $awake: boolean }>`
  display: none;

  @media (hover: none) {
    display: flex;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    flex-direction: column;
    gap: 10px;
    z-index: 11;
    opacity: ${props => props.$awake ? 1 : 0};
    pointer-events: ${props => props.$awake ? 'auto' : 'none'};
    transition: opacity 0.22s ease-out;
  }

  /* Reduced motion: no fade, just presence/absence. */
  @media (hover: none) and (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* Touch targets, not desktop furniture. */
  & > button {
    width: 44px;
    height: 44px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

/**
 * "12 / 40" orientation readout for the message stepper (2026-08
 * reading-comfort round). Appears with the nav controls while you are
 * stepping and fades with them, so a long thread tells you where you are
 * without adding permanent chrome.
 */
export const StepPositionBadge = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  color: #64748b;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  user-select: none;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.22s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Container for older messages that can be revealed.
 *
 * Uses instant visibility toggle - the "scroll up" motion comes from
 * the animated scroll, not from CSS expansion. This creates a more
 * natural feeling of rapidly scrolling up through chat history.
 *
 * When collapsed: height: 0, overflow: hidden, invisible
 * When expanded: height: auto, visible immediately
 */
export const AnimatedRevealContainer = styled.div<{ $expanded: boolean; $isAnimating: boolean }>`
  /* Instant height change - scroll animation provides the motion */
  height: ${props => props.$expanded ? 'auto' : '0'};
  overflow: ${props => props.$expanded ? 'visible' : 'hidden'};

  /* Very brief opacity transition for polish, but mostly instant */
  opacity: ${props => props.$expanded ? 1 : 0};
  transition: opacity 0.1s ease-out;

  /* Inner wrapper for message containment */
  & > .reveal-inner {
    /* Messages visible immediately when expanded */
    & > * {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * Divider shown between older and recent messages during/after reveal.
 * Provides visual separation with a subtle fade-in animation.
 */
export const RevealDivider = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease-out;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.08), transparent);
  }

  span {
    padding: 0 12px;
    font-size: 0.7rem;
    color: #94a3b8;
    font-weight: 500;
    text-transform: lowercase;
    letter-spacing: 0.5px;
  }
`;

/**
 * The "new messages" rule (2026-09-14).
 *
 * The bottom pill already says that new messages exist and how many; this
 * says WHERE they start, which is the half a returning reader actually has to
 * guess at. Modelled on RevealDivider's hairline-and-label shape so it reads
 * as part of the transcript, but in the brand accent rather than muted grey —
 * it is the one divider that means something.
 *
 * DELIBERATELY NOT STICKY. Chrome parked over message text is the 2026-08-14
 * complaint that put the touch stepper to sleep, and a rule that follows you
 * down the thread is exactly that. It sits still and you scroll past it.
 */
export const UnreadDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px 6px;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--brand-gold, #c9a227), transparent);
    opacity: 0.55;
  }

  span {
    padding: 0 12px;
    font-size: 0.7rem;
    color: var(--brand-ink-muted, #8a7a4a);
    font-weight: 600;
    text-transform: lowercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }
`;

/**
 * I4 ("Load earlier messages", 2026-07-15): slim in-flow affordance pinned at
 * the TOP of the message list when the thread's server history extends beyond
 * the loaded window. Deliberately quieter than the floating pills (it is
 * content-adjacent, not navigation): RevealDivider's muted typography on a
 * FloatingPillButton-style secondary chip, centered, self-sized.
 */
export const LoadEarlierMessagesButton = styled.button<{ $loading?: boolean }>`
  align-self: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 5px 14px;
  margin: 0 0 0.25rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #64748b;
  cursor: ${props => props.$loading ? 'default' : 'pointer'};
  opacity: ${props => props.$loading ? 0.65 : 1};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease-out;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: default;
  }

  @media (max-width: 600px) {
    font-size: 0.68rem;
    padding: 4px 12px;
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    font-size: 0.68rem;
    padding: 4px 12px;
  }
`;

export const ChatFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: var(--brand-canvas);
  z-index: 1;
  width: 100%;
  padding: 0;
  border-radius: 0 0 16px 16px;
  flex-shrink: 0;
  overflow: visible;

  /* Fullscreen takeover (mobile UX 2026-08-14): with the pane at inset 0,
     the composer sits on the very bottom edge — pad past the home-indicator
     safe area on notched phones. Zero everywhere else. */
  ${mediaMax.mobileViewport} {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
`;

export const ThreadChoiceOpenRow = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 8px 12px 0;
`;

export const ThreadChoiceOpenButton = styled.button`
  appearance: none;
  border: 1px solid rgba(182, 146, 76, 0.42);
  border-radius: 999px;
  background: rgba(198, 167, 94, 0.12);
  color: #5f471f;
  font-size: 0.78rem;
  line-height: 1;
  padding: 7px 12px;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  &:hover {
    background: rgba(198, 167, 94, 0.18);
    border-color: rgba(182, 146, 76, 0.62);
    color: #4f3a17;
  }

  &:focus-visible {
    outline: 2px solid rgba(182, 146, 76, 0.55);
    outline-offset: 2px;
  }
`;

// ============================================================================
// Message Components
// ============================================================================

export const MessageContainer = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
  align-items: flex-start;
  margin-bottom: 0;
  width: 100%;
`;

export const Message = styled.div<{ isUser: boolean; $animate?: boolean }>`
  max-width: 80%;
  padding: 0.82rem 0.95rem;
  border-radius: ${props => props.isUser ? '18px 18px 0 18px' : '18px 18px 18px 0'};
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${props => props.isUser ? '#ffffff' : 'var(--brand-text)'};
  background: ${props => props.isUser
    ? 'linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%) !important'
    : 'var(--brand-canvas) !important'};
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  text-transform: none;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  /* Containment for stored markup (owner report 2026-08-26): messages sent
     BEFORE the paste sanitizer stripped classes/geometry can still carry an
     inline width or nowrap from the original paste, and nothing inside a
     bubble may ever be wider than the bubble. max-width caps an inline
     width: 812px (used value = min of the two) without touching anything
     honest. Tables are exempt on purpose — they are legitimately wider than
     the bubble and scroll inside their own .chat-table-scroll wrapper. */
  & *:not(table):not(thead):not(tbody):not(tfoot):not(tr):not(td):not(th) {
    max-width: 100%;
    /* min-width BEATS max-width in CSS — a pasted inline min-width defeated
       the cap outright (2026-08-27, the giant off-screen bubble). */
    min-width: 0;
  }
  animation: ${messageFadeIn} 0.3s ease-out;
  /* Flash fix (owner UX directive 2026-08-22): the entry fade plays only for
     rows that JUST arrived ($animate from shouldAnimateMessageEntry). A row
     remounting later — merge churn, the older/recent boundary reparenting —
     renders settled and still instead of replaying from opacity 0. */
  ${props => (props.$animate === false ? 'animation: none;' : '')}
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  user-select: text;

  /* Proper paragraph spacing */
  p {
    margin: 0 0 0.75em 0;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* List spacing */
  ul, ol {
    padding-left: 1.5em;
    list-style-position: outside;
    margin: 0.5em 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Links (owner report 2026-09-12: "links are nearly the same color as the
     background of a sent message"). A link in a chat bubble had no colour of
     its OWN, so it fell all the way to the global \`a { color:
     var(--color-primary) }\` — which is the brand gold. In the viewer's own
     bubble that is the ground the link is painted ON: gold on gold, 1.00:1 at
     the gradient's light stop. The meeting link in the report was literally
     invisible; the Prest0n bubble's gold-on-canvas was only 2.21:1, below
     even the house's own illegibility floor (MIN_PASTE_CONTRAST_RATIO, 2.5).

     So each bubble names a link colour keyed to its own ground, and both
     clear WCAG AA (4.5:1) for body text — asserted as a RATIO, not a
     spelling, in chatBubbleLinkContrast.test.tsx:
       · gold bubble   → --brand-black      7.53:1 light stop / 5.97:1 dark
       · canvas bubble → --brand-gold-text  5.64:1
     Both are palette tokens, so the link stays in the two-colour brand world
     rather than importing a foreign "link blue". White was not an option for
     the gold bubble: it is the bubble's own body colour (nothing would then
     mark the link as a link) and white-on-gold is 2.31:1 — under that same
     2.5 floor. Underlined at rest, because colour must never be the only cue
     that something is clickable. */
  a {
    color: ${props => props.isUser ? 'var(--brand-black)' : 'var(--brand-gold-text)'};
    text-decoration: underline;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;

    &:hover,
    &:focus-visible {
      color: var(--brand-black);
      text-decoration-thickness: 2px;
    }
  }

  /* Chat tables (2026-08-02 Miles report): rows/columns render correctly at
     EVERY pane width. The bubble's pre-wrap/word-break rules are exactly wrong
     inside a table — they shredded cells into one-letter-per-line columns at
     docked widths. Cells keep words whole and the TABLE scrolls horizontally
     inside .chat-table-scroll (normalizeChatHtml wraps every bare table). */
  .chat-table-scroll {
    overflow-x: auto;
    max-width: 100%;
    margin: 0.6em 0;
    -webkit-overflow-scrolling: touch;
  }
  table {
    border-collapse: collapse;
    width: max-content;
    max-width: none;
    font-size: 0.86em;
    line-height: 1.35;
    white-space: normal;
  }
  th, td {
    border: 1px solid rgba(15, 23, 42, 0.16);
    padding: 5px 9px;
    text-align: left;
    vertical-align: top;
    word-break: normal;
    overflow-wrap: normal;
    white-space: normal;
    max-width: 30ch;
  }
  th {
    background: rgba(15, 23, 42, 0.05);
    font-weight: 600;
    white-space: nowrap;
  }

  /* Copyable chunks (owner directive 2026-08-17): email-draft blockquotes
     and code blocks render as visually bounded chunks a user can copy in one
     click. Like tables, both need the bubble's pre-wrap overridden — a
     blockquote's own \n→<br/> handles its line structure, and <pre> keeps its
     literal layout and scrolls horizontally instead of shredding. */
  blockquote {
    margin: 0.6em 0;
    padding: 0.55em 2.2em 0.55em 0.8em;
    border-left: 3px solid rgba(198, 167, 94, 0.85);
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.04);
    white-space: normal;

    &:last-child {
      margin-bottom: 0;
    }
  }
  pre {
    margin: 0.6em 0;
    padding: 0.65em 2.2em 0.65em 0.8em;
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.06);
    overflow-x: auto;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
    white-space: pre;

    code {
      font-family: 'SF Mono', Menlo, Consolas, monospace;
      font-size: 0.86em;
      line-height: 1.4;
      white-space: pre;
      background: none;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
  .chunk-copy-host {
    position: relative;
  }
  .chunk-copy-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 5px;
    background: transparent;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.15s ease, background 0.15s ease, transform 0.15s ease;

    svg {
      width: 12px;
      height: 12px;
      stroke: currentColor;
      stroke-width: 1.8;
      fill: none;
    }

    &:hover {
      /* SPECIAL_MESSAGE_COLORS.accent — declared below this component, so
         the literal is inlined here (template interpolation would hit TDZ). */
      color: #1C1917;
      background: rgba(15, 23, 42, 0.06);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    &.copied {
      color: #1C1917;
    }
  }

  /* Prevent excessive <br/> spacing in ASSISTANT html — hide consecutive
     breaks. USER bubbles keep every break the author typed (owner report
     2026-09-07: a blank line typed between items vanished on send). */
  ${props => props.isUser ? '' : 'br + br { display: none; }'}

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isUser
      ? '0 4px 12px rgba(198, 167, 94, 0.45)'
      : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

// Spacing constants for message footer elements
const MESSAGE_META_GAP = '8px';        // Gap between action icons (copy, info)
const MESSAGE_META_GAP_WIDE = '16px';  // Wider gap around timestamp and compute time

export const MessageTime = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
  align-items: center;
  justify-content: flex-start;
  gap: 0; /* explicit margins on children for uniform spacing */
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 0.5rem;
  text-align: right;
  user-select: none;
  /* ONE BOTTOM LINE (Miles, 2026-09-09): the reactions and the "seen by"
     marks lead this row (MetaLeading — they are the line's first items and
     never a separate row below), and the toolbar — sender chip, time with
     seconds, delivery glyph, compute time, reaction trigger, speaker, copy,
     ⓘ — follows and WRAPS between items at any width when the bubble is too
     narrow for all of it. The 2026-08-01 contract stands unchanged: every
     item is its own non-breaking unit — the bubble (Message) sets
     white-space: pre-wrap + word-break/overflow-wrap: break-word, both
     inherit into this row, and without the resets flex shrink squeezes a
     chip or a timestamp into a MID-WORD break ("Prest0 / n"). Wrapping is
     granted to the ROW between its items; no item ever breaks inside. The
     docked timestamp compacts as before (formatMessageMetaTimestamp);
     min-width: 0 + max-width: 100% keep the row from forcing the bubble
     wider. */
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
  min-width: 0;
  max-width: 100%;

  /* Icon actions (copy / ⓘ) and the compute-time pill never shrink out of
     line — the row wraps between items instead. */
  button,
  svg {
    flex-shrink: 0;
  }
`;

/**
 * The leading group of the meta line (2026-09-09, Miles: "the whom sent
 * emoji section shouldn't take more space but be a part of the bottom line
 * of a message (same with the seen messages)"): the reaction chips and the
 * seen-by marks, first in the row so they always sit on the bottom line's
 * first row; the group itself never shrinks (the toolbar wraps instead) and
 * wraps its own chips when it is wider than the bubble.
 */
export const MetaLeading = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  margin-right: 8px;
`;

/**
 * AI model processing time display (shown as clock icon + time next to message timestamp).
 * Displays the responseTimeMs from the AI model. Hover shows "compute time" tooltip.
 * Icon sized to match action buttons (14x14) for visual consistency.
 */
export const ProcessingTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
  /* 24px space between timestamp and compute time */
  margin-left: 24px;

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    opacity: 0.85;
    flex-shrink: 0;
  }

  /* Narrow pane (docked round 2, 2026-08-02): the nowrap meta row keeps its
     24px wide-mode gap everywhere else, but in a ≤480px pane that fixed gap
     is what pushes the copy/ⓘ icons toward the bubble edge — halve it. */
  @container chat-pane (max-width: 520px) {
    margin-left: 12px;
  }
`;

// ============================================================================
// Special Message Styling (Status/Progress Messages)
// ============================================================================

/**
 * CSS variables for special message theming.
 * All special messages (deep research, formatting, status updates) use this consistent palette.
 */
export const SPECIAL_MESSAGE_COLORS = {
  // Prest0 brand scheme (2026-08-12 owner directive): special-message chrome
  // rides the platform palette — warm ink + gold — never a one-off blue.
  // Distinction comes from contrast (ink on gold-tinted neutrals), not hue.
  /** Primary accent color for titles and icons */
  accent: '#1C1917',
  /** Light background for status badges/icons */
  accentBg: 'rgba(198, 167, 94, 0.14)',
  /** Subtle border for visual distinction */
  border: '#E0D5BC',
  /** Secondary text color for subtitles */
  subtitle: '#6B6459',
  /** Helper/muted text color */
  muted: 'var(--brand-text-muted)',
} as const;

/**
 * Styled wrapper for special status messages (deep research, formatting, etc.)
 * Provides a consistent visual treatment with a subtle left accent border.
 */
export const SpecialMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 12px;
  border-left: 3px solid ${SPECIAL_MESSAGE_COLORS.border};
  margin: 4px 0;
`;

/**
 * Status title with icon support.
 */
export const StatusTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${SPECIAL_MESSAGE_COLORS.accent};
  font-size: 0.95rem;
`;

/**
 * Circular status icon badge.
 */
export const StatusIconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${SPECIAL_MESSAGE_COLORS.accentBg};
  color: ${SPECIAL_MESSAGE_COLORS.accent};
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

/**
 * Subtitle/description text for status messages.
 */
export const StatusSubtitle = styled.span`
  color: ${SPECIAL_MESSAGE_COLORS.subtitle};
  font-size: 0.9rem;
  line-height: 1.5;
`;

/**
 * Helper/muted text for additional context.
 */
export const StatusHelperText = styled.p`
  margin: 8px 0 0 0;
  color: ${SPECIAL_MESSAGE_COLORS.muted};
  font-size: 0.8rem;
  line-height: 1.4;
`;

/**
 * Tiny copy icon button used beside timestamps.
 */
export const CopyIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  padding: 0;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
  flex-shrink: 0;
  /* 16px space to the left (after compute time) */
  margin-left: ${MESSAGE_META_GAP_WIDE};

  /* Narrow pane (docked round 2, 2026-08-02): same meta-row rhythm trim as
     ProcessingTime — the icons keep their size (hit target), only the
     wide-mode gap compacts. */
  @container chat-pane (max-width: 520px) {
    margin-left: 10px;
  }

  &:hover {
    color: ${SPECIAL_MESSAGE_COLORS.accent};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 1.8;
    fill: none;
  }
`;

/**
 * Emoji reactions (PR-4, 2026-09-09): the "add a reaction" trigger in the
 * meta row, styled like CopyIconButton. VISIBLE AT REST on every pointer
 * (Miles, 2026-09-09: "the seen, delivered, and add reaction icons and sound
 * icon all need to be default visible and not invisible until the user
 * hovers over it") — the earlier hover reveal and the coarse-pointer hide
 * are gone; a long-press on the bubble still opens the picker as a second
 * way in on touch.
 */
export const ReactionTriggerButton = styled(CopyIconButton)`
  svg {
    width: 13px;
    height: 13px;
  }
`;

/**
 * Reply to a specific message (2026-09-15): the ↩ in the meta row, FIRST among
 * the toolbar icons — answering is the most common thing anyone does with a
 * message. Same skin as its siblings, visible at rest (the 2026-09-09 rule);
 * `aria-pressed` while this message is the composer's pending target.
 */
export const ReplyIconButton = styled(CopyIconButton)`
  svg {
    width: 13px;
    height: 13px;
  }

  &[aria-pressed='true'] {
    color: var(--brand-gold-text);
  }
`;

/**
 * Dictation (2026-09-09, Miles): the microphone in the composer toolkit.
 *
 * The paperclip's own geometry (ActionButton) so the pair reads as one toolkit,
 * with two states worth seeing across a room: gold-outlined while the browser
 * is asking for permission, gold-FILLED and breathing while it is listening.
 * The pulse is the thing that says "this is live" on a phone, where the strip
 * above may be the only other clue and a thumb is covering half the screen.
 */
export const micPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(198, 167, 94, 0.45); }
  70%  { box-shadow: 0 0 0 7px rgba(198, 167, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(198, 167, 94, 0); }
`;

/**
 * Read aloud (2026-09-09, Miles): the per-message speaker in the meta row.
 *
 * VISIBLE AT REST on every pointer (Miles, 2026-09-09: "the seen, delivered,
 * and add reaction icons and sound icon all need to be default visible") — the
 * phone is exactly where somebody wants a message read to them, and on a
 * desktop a control you cannot see is a control you do not use. The meta row
 * wraps between its items at any width, so one more always-on 13px glyph has
 * somewhere to go.
 *
 * While a readout is playing the control goes gold and breathes, so the message
 * being read is obvious in a long transcript. Colours are brand tokens only
 * (check-design-tokens.mjs).
 */
export const readAloudPulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.55; }
  100% { opacity: 1; }
`;

/** The band that travels while the voice is being made — see ReadAloudProgressIndeterminate. */
export const readAloudSweep = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

export const ReadAloudButton = styled(CopyIconButton)<{ $state?: 'idle' | 'loading' | 'playing' | 'paused' }>`
  color: ${(p) => (p.$state && p.$state !== 'idle' ? 'var(--brand-gold)' : 'rgba(0, 0, 0, 0.45)')};

  &:hover {
    color: var(--brand-gold-hover);
  }

  ${(p) => (p.$state === 'loading' || p.$state === 'playing'
    ? css`animation: ${readAloudPulse} 1.6s ease-in-out infinite;`
    : '')}

  svg {
    width: 13px;
    height: 13px;
  }
`;

/**
 * The readout player (2026-09-10, Miles: "when the user continues to hover
 * while playing it needs to allow the user skip between summarized sections
 * moving back and fourth, pause or play or restart, slow down or speed up
 * audio in increments of 10% points at a time, all in an extremely great user
 * experience").
 *
 * It appears on the row being read — while the voice is getting ready, playing
 * or paused — and NOT only while the pointer is over the bubble: losing the
 * controls because a hand drifted is exactly the small cruelty this pass is
 * removing. On every other row the meta line is unchanged.
 *
 * A solid pill like the reaction chip, so the cluster reads on the gold bubble
 * and on the grey one; it is one item of the meta line, so it wraps as a unit
 * rather than pushing the timestamp onto its own row.
 */
export const ReadAloudControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 6px 0 2px;
  padding: 6px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(0, 0, 0, 0.14);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  /* The bar is the reader's, not the bubble's: it keeps its own light ground on
     the gold user bubble and on the grey assistant one, so every control in it
     reads at the same contrast wherever it appears. */
  @container chat-pane (max-width: 420px) {
    flex-wrap: wrap;
    row-gap: 6px;
  }
`;

/**
 * A control in the bar. Bigger than the meta line's 12 px icons on purpose:
 * this is a media player a person aims at while listening, not a row of
 * decorations (2026-09-10).
 */
export const ReadAloudControlButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(0, 0, 0, 0.66);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
    color: var(--brand-gold-hover);
  }

  &:disabled {
    color: rgba(0, 0, 0, 0.26);
    cursor: default;
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

/** The play/pause control, a touch larger — it is the one being aimed at. */
export const ReadAloudPrimaryButton = styled(ReadAloudControlButton)`
  width: 30px;
  height: 30px;
  background: var(--brand-gold-text);
  color: #fff;

  &:hover:not(:disabled) {
    background: var(--brand-gold-hover);
    color: #fff;
  }

  &:disabled {
    background: rgba(0, 0, 0, 0.12);
    color: rgba(255, 255, 255, 0.85);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * How far along the reading is — the whole readout, not our chunking of it.
 * Takes the slack in the bar so it is the thing that grows on a wide pane.
 */
export const ReadAloudProgressTrack = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 60px;
  height: 4px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.14);
  overflow: hidden;
`;

export const ReadAloudProgressFill = styled.div<{ $fraction: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(p) => Math.round(Math.min(1, Math.max(0, p.$fraction)) * 100)}%;
  border-radius: 999px;
  background: var(--brand-gold-text);
  transition: width 220ms linear;
`;

/**
 * The same track while the voice is still being made: a band that travels, so
 * the bar is visibly WORKING rather than sitting at zero. Paired with words —
 * motion alone is not an explanation (2026-09-10, Miles: "it seems to do
 * nothing ... needs to [say something] while the user waits").
 */
export const ReadAloudProgressIndeterminate = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 40%;
  border-radius: 999px;
  background: var(--brand-gold-text);
  animation: ${readAloudSweep} 1.1s ease-in-out infinite;
`;

/** "getting the voice ready…" / the elapsed clock. */
export const ReadAloudStatus = styled.span`
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
`;

/** The speed cluster keeps together when the bar wraps on a narrow pane. */
export const ReadAloudSpeedGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
`;

/**
 * Speed, as a control that actually slides (2026-09-10). The first version was
 * 56 px wide across sixteen steps — three and a half pixels per step under a
 * ten-pixel thumb — so it snapped between values instead of sliding, which is
 * exactly what Miles reported: "it doesn't really slide but it does go faster
 * or slower". It now has real length, a thumb big enough to grab, and a track
 * that fills behind it so the position is readable at a glance.
 */
export const ReadAloudSpeedSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 104px;
  min-width: 84px;
  height: 16px;
  margin: 0;
  background: transparent;
  cursor: pointer;
  accent-color: var(--brand-gold-text);
  flex: 0 1 auto;

  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
  }

  &::-moz-range-track {
    height: 4px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    margin-top: -5px;
    border-radius: 50%;
    background: var(--brand-gold-text);
    border: 2px solid #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--brand-gold-text);
    border: 2px solid #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

/** "1.2x" beside the slider — the number the reader is setting. */
export const ReadAloudMeter = styled.span`
  flex: 0 0 auto;
  min-width: 30px;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.66);
  white-space: nowrap;
`;

/**
 * The one line a readout leaves behind when it could not happen — the server's
 * own D9 notice (no permission, the day's reading time used, an empty wallet,
 * nothing to read). Muted, under the bubble it belongs to, gone on the next
 * press. Never an alert, never a modal: the reader asked for a small thing and
 * gets a small answer.
 */
export const ReadAloudNotice = styled.div<{ $align?: 'left' | 'right'; $measure?: 'compact' | 'full' }>`
  margin-top: 4px;
  font-size: 0.72rem;
  line-height: 1.35;
  color: var(--brand-text-secondary);
  text-align: ${(p) => (p.$align === 'right' ? 'right' : 'left')};
  /*
    TWO MEASURES (2026-09-17).

    'compact' (34ch, the default) is unchanged and is right for the annotation
    under a single message bubble, which it was tuned for: a short line, aligned
    to the bubble it belongs to.

    'full' is for a notice in the COMPOSER band, which is a different thing — an
    INSTRUCTION somebody has to act on. The mic-blocked line is 128 characters,
    and at 34ch it becomes a four-line ribbon in a band that has the whole
    composer's width available. 52ch stays inside the readable measure while
    nearly halving the line count, and the 100% keeps it honest on a phone.

    Never nowrap/ellipsis in either: an instruction cut off mid-word tells the
    reader less than nothing, and the dictation strip's Hint next door is the
    only place that treatment belongs (a live status line, where the tail is
    what matters).

    NOTE: no backticks in this comment — it lives inside a template literal, and
    one would end the string. (Cost me a red typecheck writing it.)
  */
  max-width: ${(p) => (p.$measure === 'full' ? 'min(52ch, 100%)' : '34ch')};
  ${(p) => (p.$align === 'right' ? 'margin-left: auto;' : '')}
`;

// ============================================================================
// Token Info Components
// ============================================================================

/**
 * Animation for token info popup appearance.
 */
export const tokenInfoFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

/**
 * Tiny token info icon button used beside timestamps.
 * Matches CopyIconButton styling for consistency.
 */
export const TokenInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  padding: 0;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
  position: relative;
  flex-shrink: 0;
  margin-left: ${MESSAGE_META_GAP};

  &:hover {
    color: ${SPECIAL_MESSAGE_COLORS.accent};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 1.8;
    fill: none;
  }
`;

/**
 * Floating popup container for token usage information.
 * Uses glassmorphism design with backdrop blur for a modern look.
 * All text is displayed in lowercase for visual consistency.
 * Dynamic width adapts to content - uses horizontal space strategically.
 */
export const TokenInfoPopup = styled.div<{ $position?: 'above' | 'below' }>`
  position: fixed;
  /* Body-portaled: must clear the fullscreen chat container (see
     BODY_PORTAL_Z_INDEX doc). 600000 buried it in expanded mode. */
  z-index: ${BODY_PORTAL_Z_INDEX};
  /* Viewport-bounded width (owner mobile report 2026-08-12): the popup was a
     flat 280-420px, so on a ~360px phone it was simply WIDER than the screen
     — the JS positioner clamps the LEFT offset but can never rescue a box
     that does not fit, and the cost column rendered off the right edge. Both
     now collapse to the viewport (24px of breathing room) before the
     positioner runs, so the clamp lands it fully on-screen every time. */
  min-width: min(280px, calc(100vw - 24px));
  max-width: min(420px, calc(100vw - 24px));
  width: max-content;
  box-sizing: border-box;

  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.15),
    0 8px 20px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset;
  padding: 18px 20px;
  animation: ${tokenInfoFadeIn} 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  pointer-events: auto;
  text-transform: lowercase;

  display: flex;
  flex-direction: column;
  gap: 0;

  /* R6 (2026-08-03, owner directive): multi-call turns can carry MANY
     per-call sections — the popup must scroll internally, never overflow
     the viewport or crush its content. */
  max-height: min(68vh, 560px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.18);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

/**
 * Header section of the token info popup with provider badge.
 */
export const TokenInfoHeader = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$compact ? '8px' : '10px'};
  margin-bottom: ${props => props.$compact ? '8px' : '12px'};
  padding-bottom: ${props => props.$compact ? '6px' : '10px'};
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

/**
 * Provider badge with icon and name.
 */
export const TokenInfoProviderBadge = styled.div<{ $provider?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0.03em;
  background: ${props => {
    switch (props.$provider) {
      case 'perplexity': return 'linear-gradient(135deg, #1a1a2e, #16213e)';
      case 'claude': return 'linear-gradient(135deg, #d4a574, #c9956c)';
      case 'openai': return 'linear-gradient(135deg, #10a37f, #0d8f6f)';
      default: return 'linear-gradient(135deg, #64748b, #475569)';
    }
  }};
  color: white;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`;

/**
 * Model name display within header.
 */
export const TokenInfoModel = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
`;

/**
 * Grid layout for token counts.
 * Horizontal row layout for compact display.
 */
export const TokenInfoGrid = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.$compact ? '4px' : '8px'};
  margin-bottom: 2px;
`;

/**
 * Individual token count item.
 * Compact horizontal layout with centered content.
 */
export const TokenInfoItem = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${props => props.$compact ? '2px' : '3px'};
  padding: ${props => props.$compact ? '6px 10px' : '10px 14px'};
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.035) 100%);
  border-radius: ${props => props.$compact ? '6px' : '8px'};
  border: 1px solid rgba(0, 0, 0, 0.04);
  min-width: ${props => props.$compact ? '55px' : '65px'};
  flex: 1;
  transition: background 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.045) 100%);
  }
`;

/**
 * Label for token count.
 */
export const TokenInfoLabel = styled.span<{ $compact?: boolean }>`
  font-size: ${props => props.$compact ? '0.6rem' : '0.65rem'};
  color: #94a3b8;
  letter-spacing: 0.03em;
  font-weight: 500;
  text-transform: lowercase;
`;

/**
 * Value for token count with emphasized styling.
 */
export const TokenInfoValue = styled.span<{ $compact?: boolean }>`
  font-size: ${props => props.$compact ? '0.85rem' : '0.95rem'};
  font-weight: 600;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

/**
 * Footer section with response time and timestamp.
 */
export const TokenInfoFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.7rem;
  color: #94a3b8;
`;

/**
 * Response time display with icon.
 */
export const TokenInfoResponseTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`;

/**
 * Cost breakdown section in token popup.
 * Compact layout using horizontal space efficiently.
 */
export const TokenInfoCostSection = styled.div<{ $compact?: boolean }>`
  margin-top: ${props => props.$compact ? '8px' : '12px'};
  padding-top: ${props => props.$compact ? '8px' : '12px'};
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

export const TokenInfoCostHeader = styled.div<{ $compact?: boolean }>`
  font-size: ${props => props.$compact ? '0.68rem' : '0.72rem'};
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.5px;
  margin-bottom: ${props => props.$compact ? '6px' : '10px'};
`;

/**
 * Cost grid with 2-column layout for horizontal efficiency.
 */
export const TokenInfoCostGrid = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.$compact ? '4px 10px' : '6px 16px'};

  /* Phone widths (owner mobile report 2026-08-12): two cost columns inside a
     viewport-bounded popup leave ~140px each, which truncates the amounts.
     One column reads cleanly and the popup simply grows a little taller. */
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Individual cost row - compact inline layout.
 */
export const TokenInfoCostRow = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.$compact ? '3px 8px' : '5px 10px'};
  background: rgba(0, 0, 0, 0.015);
  border-radius: ${props => props.$compact ? '4px' : '6px'};
  transition: background 0.12s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
`;

export const TokenInfoCostLabel = styled.span<{ $compact?: boolean }>`
  color: #64748b;
  font-size: ${props => props.$compact ? '0.68rem' : '0.72rem'};
  font-weight: 450;
`;

export const TokenInfoCostValue = styled.div<{ $compact?: boolean }>`
  color: #0f172a;
  font-weight: 550;
  font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
  font-size: ${props => props.$compact ? '0.68rem' : '0.72rem'};
  letter-spacing: -0.01em;
  margin-left: ${props => props.$compact ? '4px' : '8px'};
`;

/**
 * Total cost row with prominent styling - spans full width.
 */
export const TokenInfoCostTotal = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${props => props.$compact ? '6px' : '10px'};
  padding: ${props => props.$compact ? '6px 10px' : '10px 12px'};
  background: linear-gradient(135deg, rgba(5, 150, 105, 0.06) 0%, rgba(5, 150, 105, 0.1) 100%);
  border-radius: ${props => props.$compact ? '6px' : '8px'};
  border: 1px solid rgba(5, 150, 105, 0.12);
  grid-column: 1 / -1;
`;

export const TokenInfoCostTotalLabel = styled.span<{ $compact?: boolean }>`
  color: #065f46;
  font-size: ${props => props.$compact ? '0.72rem' : '0.78rem'};
  font-weight: 600;
  text-transform: lowercase;
`;

export const TokenInfoCostTotalValue = styled.div<{ $compact?: boolean }>`
  color: #059669;
  font-size: ${props => props.$compact ? '0.8rem' : '0.88rem'};
  font-weight: 700;
  font-family: 'SF Mono', 'Menlo', 'Monaco', monospace;
  letter-spacing: -0.02em;
`;

/**
 * Container for message actions (copy button, token info button, etc.)
 * Provides relative positioning context for popup.
 * Uses consistent 8px gap for even spacing with other footer elements.
 */
export const MessageActionsContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${MESSAGE_META_GAP};
`;

// ============================================================================
// Prompt Bar Components
// ============================================================================

export const PromptBarContainer = styled.div<{ $alignRight?: boolean }>`
  position: fixed;
  bottom: calc(var(--prompt-bar-gap, 1.5rem) + env(safe-area-inset-bottom, 0px));
  left: ${props => props.$alignRight ? 'auto' : '50%'};
  right: ${props => props.$alignRight ? '24px' : 'auto'};
  transform: ${props => props.$alignRight ? 'translateY(0)' : 'translateX(-50%)'};
  width: 90%;
  max-width: 800px;
  background: var(--brand-canvas);
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid rgba(0,0,0,0.05);
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  min-height: 56px;
  --prompt-bar-height: 56px;
  z-index: ${CHAT_PROMPT_BAR_Z_INDEX};
  animation: ${props => props.$alignRight ? fadeInDocked : fadeInCentered} 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 1;
  visibility: visible;
  transition: opacity 0.2s ease, box-shadow 0.2s ease, transform 0.25s ease, right 0.25s ease, left 0.25s ease;
  flex-wrap: nowrap;
  overflow: visible;

  /* Narrow PANE (desktop docked 420px included): the viewport queries below
     never fire there. Only the in-chat instance lives inside the chat-pane
     container (the floating closed-state bar matches no container, so the
     viewport rules govern it). Horizontal compactness only — vertical
     growth caps stay viewport-keyed. DECLARED FIRST so that on a phone —
     where the pane query AND the viewport queries both match (fullscreen
     ChatContainer is also the chat-pane container) — the phone tiers below
     win the shared properties by source order (review 2026-08-14). */
  @container chat-pane (max-width: 520px) {
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    min-height: 48px;
  }

  @media (max-width: 900px) {
    width: 95%;
  }

  /* The 600px tier that used to sit here was DEAD CODE (measured 2026-09-13):
     the phone tier below is declared after it and restates all four of its
     properties, so it won every viewport 600 and under. Removed rather than
     tuned — a rule that cannot fire is a rule that misleads whoever reads the
     file next while they work out why their change did nothing. */

  ${mediaMax.mobileViewport} {
    flex-direction: row;
    /* nowrap, unconditionally (2026-08-14, restored 2026-09-13). The rule that
       stopped a fixed-width mention chip from starving the typing area depends
       on it. The 2026-09-12 controls-drop-below layout punched a conditional
       hole in it; with the controls back on one row there is nothing left to
       wrap, and a composer that can grow a second row is a composer that will. */
    flex-wrap: nowrap;
    align-items: flex-end;
    justify-content: center;
    gap: 0.5rem;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    /* Full usable width on phones. The old 480px cap plus a fixed-width
       mention chip squeezed the typing area to ~40px on a 375px screen
       (owner report 2026-08-14, fold-phone screenshots). */
    width: calc(100% - 1.5rem);
    max-width: none;
    padding: 0.5rem 0.625rem;
    min-height: 48px;

    & > [data-role="prompt-input"] {
      flex: 1 1 auto;
      min-width: 0;
    }

    /* The mention chip is excluded: it has its own phone rule (it shrinks,
       ellipsizing the name) so it never starves the typing area. Prefix
       match (^=) because there are TWO chip instances — "mention-tab-chip"
       in-chat and "mention-tab-chip-floating" on the closed pill — and an
       exact match left the floating one unshrinkable (review 2026-08-14). */
    /* 44px, not 40 (2026-09-13). This rule and ComposerControls' twin below
       BOTH sized the composer's buttons, and both undercut ActionButton's
       deliberate 44px Apple minimum — so restoring it in one place alone was
       silently reverted by the other. Measured before the fix: every visible
       composer control was 40px at 390, 360 and 320. */
    & > button:not([data-testid^="mention-tab-chip"]) {
      flex: 0 0 auto;
      width: auto;
      min-width: 44px;
      height: 44px;
      justify-content: center;
    }
  }

  /* Fold-phone cover screens and other ultra-narrow viewports. */
  @media (max-width: 360px) {
    width: calc(100% - 1rem);
    padding: 0.5rem;
    gap: 0.375rem;
  }
`;

export const PromptInputWrapper = styled.div<{ $collapsed?: boolean }>`
  flex: 1;
  display: grid;
  min-width: 0;
  min-height: 44px;
  max-height: ${props => props.$collapsed ? '44px' : '360px'};

  &::after,
  & > div {
    grid-area: 1 / 1 / 2 / 2;
    padding: 0.625rem 0.875rem;
    border: 1px solid var(--brand-line);
    border-radius: 10px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: ${props => props.$collapsed ? 'nowrap' : 'pre-wrap'};
    word-wrap: break-word;
    overflow-wrap: break-word;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: ${props => props.$collapsed ? 'hidden' : 'auto'};
    text-overflow: ${props => props.$collapsed ? 'ellipsis' : 'clip'};
  }

  &::after {
    content: attr(data-replicated-value) ' ';
    visibility: hidden;
    pointer-events: none;
    min-height: 44px;
    max-height: ${props => props.$collapsed ? '44px' : '360px'};
  }

  ${mediaMax.mobileViewport} {
    min-height: 40px;
    /* Room to write on phones: grow to ~a third of the visible viewport
       (dvh tracks browser chrome/keyboard) instead of the old 160px cap. */
    max-height: ${props => props.$collapsed ? '40px' : '240px'};
    max-height: ${props => props.$collapsed ? '40px' : 'min(280px, 38dvh)'};
    &::after,
    & > div {
      padding: 0.5rem 0.75rem;
      /* 16px minimum — anything smaller makes iOS Safari zoom the page on
         focus, which is the #1 "the composer is broken on my phone" report. */
      font-size: 1rem;
      min-height: 40px;
    }
    &::after {
      max-height: ${props => props.$collapsed ? '40px' : '240px'};
      max-height: ${props => props.$collapsed ? '40px' : 'min(280px, 38dvh)'};
    }
  }

  /* Narrow pane (desktop docked included): horizontal compactness only —
     the tall docked pane keeps the default vertical growth caps, and the
     font size is NOT reduced here so the 16px no-zoom floor above survives
     when the pane query also matches on a phone. */
  @container chat-pane (max-width: 520px) {
    &::after,
    & > div {
      padding: 0.5rem 0.75rem;
    }
  }
`;

export const PromptInput = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 44px;
  max-height: 360px;
  resize: none;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--brand-canvas);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  box-sizing: border-box;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  border: none;
  position: relative;
  direction: ltr;
  text-align: left;
  caret-color: #0f172a;
  unicode-bidi: plaintext;
  text-transform: none;

  /* HARD CONTAINMENT of pasted markup (owner report 2026-08-26: a copied
     message pasted back rendered as a bubble hanging off the right edge).
     The sanitizer now strips classes and geometry at paste time; these rules
     are the second wall, so markup already sitting in drafts from before the
     fix — or arriving through any path that skips the sanitizer — still
     cannot widen the composer or repaint itself as a bubble. Descendants may
     not exceed the composer's width, force their own wrapping mode, or carry
     a box of their own. */
  & * {
    max-width: 100% !important;
    width: auto !important;
    min-width: 0 !important;
    white-space: inherit !important;
    background: transparent !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    float: none !important;
    position: static !important;
  }
  /* Lists keep their own indent — it is the one 'margin' that is meaning. */
  & ul, & ol {
    padding-left: 1.25em !important;
  }

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.25);
  }

  &:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    pointer-events: none;
    /* Single-line ellipsized placeholder at EVERY width (was confined to a
       <=720px viewport query, so the 65-char default placeholder wrapped to
       2-3 lines in the ~266px-wide docked composer and ballooned the empty
       composer's height — docked-mode UX, 2026-07-13). */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    max-width: 100%;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  ${mediaMax.mobileViewport} {
    min-height: 40px;
    max-height: 240px;
    max-height: min(280px, 38dvh);
  }
`;

// ============================================================================
// Button Components
// ============================================================================

/**
 * The composer's control cluster — attachment, microphone, send (2026-09-12).
 *
 * Owner: "the user should have more horizontal space when typing in mobile
 * view (desktop view is fine) so make the send, microphone, and attachment
 * buttons go up vertically in mobile view so as to give more space for an
 * ever growing text draft box."
 *
 * DESKTOP IS `display: contents`, AND THAT IS THE WHOLE TRICK. The three
 * buttons stay DIRECT flex children of PromptBarContainer exactly as before,
 * so every rule already written against `& > button` there still matches and
 * desktop renders byte for byte what it rendered yesterday. The wrapper only
 * becomes a box where a phone query says so.
 *
 * Two phone layouts, chosen by the draft's own height (see
 * composerControlsLayout.ts for the hysteresis that keeps the switch from
 * flickering mid-sentence):
 *
 *   · stacked  — one narrow column, bottom-aligned, so the controls rise as
 *     the box grows and the text gains ~2 button-widths of width;
 *   · dropped  — their own full-width row beneath the box, which then spans
 *     the entire composer.
 *
 * Reading order is preserved in both: attachment, microphone, send. Send ends
 * up nearest the thumb in the column and at the trailing edge in the row.
 *
 * NO TRANSITION on the switch, deliberately. An animation that runs while the
 * on-screen keyboard is up and the textarea is mid-resize is precisely what
 * reads as jumpy; the layouts swap in one frame instead.
 */
/**
 * THE MINIMIZED CHAT, ON A PHONE (2026-09-13).
 *
 * Owner report, with a screenshot: "when the thread is at the bottom and
 * minimized… it's not really clear what that element is. It should be clear
 * where the user clicks for the chat interface. Maybe when minimized in mobile
 * view, it's in the bottom right hand corner, in a clear chat bubble icon, in
 * the yellow color scheme."
 *
 * What was there: a full-width bar carrying a thread chip, a "+", a text box
 * and a send arrow — no glyph, no name, no avatar, nothing that said "chat".
 * Measured at 390px it was 103px tall with a 47px typing area.
 *
 * WHO SEES THIS, AND WHY IT IS NOT EVERYONE. The bubble is right for a member
 * and wrong for a stranger: on `/` the closed bar is the guest funnel AND the
 * surface carrying the Cal. B&P section 17941(b) disclosure, neither of which
 * fits in a 56px circle. So the split is by VIEWER, not by viewport, and the
 * gate is useViewerIsSignedOut — the same hook the disclosure self-gates on, so
 * the two can never disagree about which surface is which.
 *
 * COLOUR IS NOT A CHOICE HERE. theme.ts, 2026-09-12: gold is a FILL, never text
 * on a light ground. White on --brand-gold measures 2.31:1; --brand-charcoal on
 * it is 6.1:1. The glyph is charcoal.
 */
export const ChatLauncherBubble = styled.button`
  position: fixed;
  right: max(1rem, env(safe-area-inset-right, 0px));
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-gold);
  color: var(--brand-charcoal);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
  cursor: pointer;
  z-index: ${CHAT_LAUNCHER_Z_INDEX};
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;

  svg {
    width: 26px;
    height: 26px;
  }

  &:hover {
    background: var(--brand-gold-hover);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.26);
  }

  &:active {
    transform: scale(0.94);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Where the bot-disclosure line lives (2026-09-13).
 *
 * It is the Cal. B&P section 17941(b) safe harbour for a signed-out visitor and
 * it is not going anywhere — but it was rendered as a bare flex SIBLING inside
 * the prompt bar's row, so it competed with the text box for horizontal space.
 * Measured on the deployed build at 390px: a 75x85 column beside the input,
 * which was left with FORTY-SEVEN pixels; at 320px, twenty-four. In the open
 * composer it took 221px of a 346px row.
 *
 * A caption belongs under the thing it captions, and the fix is to get it out
 * of the row rather than to give it a bigger share of one:
 *
 *   · in the OPEN composer it is a sibling rendered after the bar, in flow,
 *     one short line beneath it;
 *   · on the CLOSED launcher there is no room even for that, so it is
 *     absolutely positioned just above the pill.
 *
 * It renders null for a member, so a signed-in user pays nothing for either.
 */
export const ComposerDisclosureSlot = styled.div<{ $floating?: boolean }>`
  min-width: 0;
  width: 100%;

  ${props => props.$floating && `
    /* Absolutely positioned, which also takes it OUT of the flex layout — an
       absolutely positioned child of a flex container is not a flex item, so
       it cannot take a share of the row no matter what its basis says. That
       is load-bearing here: the first attempt put an in-flow flex-basis:100%
       item in a nowrap row and squeezed the text box to 30px at every width,
       desktop included. Measured, then fixed by taking it out of flow. */
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(100% + 4px);
    pointer-events: none;
  `}
`;

export const ComposerControls = styled.div<{ $tall?: boolean }>`
  display: contents;

  ${mediaMax.mobileViewport} {
    display: flex;
    flex: 0 0 auto;
    min-width: 0;
    gap: 0.375rem;
    align-items: center;

    /*
      ONE ROW, ALWAYS (2026-09-13).

      This wrapper shipped on 2026-09-12 answering an owner report asking for
      more horizontal room while typing: with a short draft the three controls
      stacked in a narrow COLUMN beside the box, and with a tall one they
      dropped to their own row beneath it.

      Measured a day later, on the deployed build: the column made the composer
      162px tall for an EMPTY draft — three 40px buttons and two gaps — on every
      phone. align-items: flex-end on the bar means the tallest child sets the
      row height, and the column was always the tallest child. The owner's next
      report was the other half of the same complaint: "when a user is typing a
      short message, it kinda goes too far up a screen and waste space… give
      ample room for the thread to be able to be shown."

      Both asks are satisfied at once by removing a control from the row rather
      than by stacking it. The trailing slot now carries the microphone when
      there is nothing to send and the send button the moment there is
      (composerControlsLayout.ts composerTrailingControl — the WhatsApp rule),
      so the row holds TWO 44px controls instead of three 40px ones. At 390px
      that is more typing width than the row-of-three it replaces and ~100px of
      height back; the column still wins on width alone, which is the trade the
      owner reversed having seen it.

      $tall is kept in the signature and deliberately unused for layout: the
      hysteresis it comes from is still exported and still tested, so restoring
      the column is a small diff rather than an excavation. See COMPOSER_ONE_ROW.
    */
    flex-direction: row;
    justify-content: flex-end;
    align-self: flex-end;

    /* 44px, not 40. PromptBarContainer's phone tier carries the same rule for
       its DIRECT children; these buttons are not direct children, so the floor
       has to be restated here or ActionButton's 44px is undone for exactly the
       controls a thumb has to hit. Both producers move together — that is the
       whole reason this comment exists in two places. */
    & button {
      flex: 0 0 auto;
      width: auto;
      min-width: 44px;
      height: 44px;
      justify-content: center;
    }
  }
`;

export const ActionButton = styled.button`
  padding: 0.5rem;
  border: 1px solid var(--brand-line);
  border-radius: 6px;
  background: white;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  /*
    44px, not 40 (2026-09-11). The 2026-09 mobile-responsive pass raised the
    header and footer tap targets to Apple's 44px minimum and never reached
    the composer toolkit, so the paperclip — the control a phone user has to
    hit to send a photo at all — stayed under it. The icon inside is
    unchanged, so nothing looks bigger; only the hit area is.
  */
  height: 44px;
  width: 44px;

  &:hover {
    background: var(--brand-surface);
    border-color: var(--brand-line-strong);
    color: var(--brand-text);
  }

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 1.75;
    opacity: 0.8;
    transition: all 0.2s ease;
  }

  &:hover svg {
    opacity: 1;
  }
`;

/**
 * One-keystroke mention affordance (owner directives 2026-08-11 → 2026-08-12
 * placement/polish round): a compact pill at the LEFT of the draft input in
 * mention-gated group threads — where the mention will land, so the affordance
 * reads as part of composing rather than another action button on the right.
 * Shows WHO one click (or one Tab press) will mention, with an icon-only ⇥
 * keycap disclosing the shortcut — no written-out "tab". Visual language
 * matches the house chips (StarterChip's warm-gold pill family) at the 40px
 * composer-row rhythm, so it slots into the flex row with no layout surgery.
 */
export const MentionTabChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 8px 0 14px;
  flex: 0 0 auto;
  border-radius: 20px;
  border: 1px solid rgba(198, 167, 94, 0.4);
  background: #FBF7EE;
  color: #8a6d2f;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;

  /* Phones (owner directive 2026-08-14: "the user always needs plenty of
     space to type"): the chip collapses to an ICON-ONLY @ stub — a fixed
     40px target that cedes every other pixel to the input. The person's
     name stays in the aria-label/title; font-size: 0 hides the text node,
     the ::before supplies the glyph, and the ⇥ keycap (a keyboard
     affordance) hides entirely. Supersedes the earlier shrink-to-35% rule,
     which still let a long name eat ~120px of a 375px screen. */
  ${mediaMax.mobileViewport} {
    flex: 0 0 auto;
    min-width: 40px;
    max-width: 44px;
    height: 40px;
    padding: 0;
    justify-content: center;
    gap: 0;
    font-size: 0;

    &::before {
      content: '@';
      font-size: 17px;
      font-weight: 700;
      line-height: 1;
    }

    & > span {
      display: none;
    }
  }

  &:hover {
    background: #F5EFE0;
    border-color: rgba(198, 167, 94, 0.7);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 2px;
  }
`;

/**
 * The ⇥ keycap inside MentionTabChip — icon only, keyboard-key shaped (small
 * rounded square with a bottom edge, the modern web keycap idiom).
 */
export const MentionTabKeycap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 4px;
  border-radius: 6px;
  border: 1px solid rgba(138, 109, 47, 0.28);
  border-bottom-width: 2px;
  background: rgba(255, 255, 255, 0.85);
  color: #8a6d2f;
  font-size: 13px;
  line-height: 1;
`;

/**
 * The composer microphone. See micPulse above for why the states look as they do.
 * Colours are brand tokens only (check-design-tokens.mjs).
 */
export const DictationMicButton = styled(ActionButton)<{ $phase?: 'idle' | 'requesting' | 'listening' }>`
  background: ${(p) => (p.$phase === 'listening' ? 'var(--brand-gold)' : 'var(--brand-surface)')};
  border-color: ${(p) => (p.$phase && p.$phase !== 'idle' ? 'var(--brand-gold)' : 'var(--brand-line)')};
  color: ${(p) => {
    if (p.$phase === 'listening') return 'var(--brand-gold-text)';
    return p.$phase === 'requesting' ? 'var(--brand-gold)' : 'var(--brand-text-secondary)';
  }};

  &:hover {
    border-color: var(--brand-gold);
    color: ${(p) => (p.$phase === 'listening' ? 'var(--brand-gold-text)' : 'var(--brand-gold)')};
  }

  ${(p) => (p.$phase === 'listening' ? css`animation: ${micPulse} 1.6s ease-in-out infinite;` : '')}
`;

export const SendButton = styled(ActionButton)`
  background: var(--brand-black);
  color: white;
  border: none;

  &:hover {
    background: var(--brand-charcoal);
    color: white;
  }

  svg {
    opacity: 1;
  }
`;

export const HideButton = styled.button`
  background: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.06);
  color: #444;
  padding: 0.4rem 0.75rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  text-transform: lowercase;

  &:hover {
    background: #eee;
    color: #222;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2;
  }
`;

/**
 * Compact icon-only button for chat header actions (fullscreen, dock, hide).
 * Uniform 32x32px square with native title tooltip support.
 */
export const HeaderIconButton = styled.button`
  /* Anchor for the unseen-activity dot the notify bell / ⋯ wear (2026-09-08). */
  position: relative;
  width: 32px;
  height: 32px;
  /* Invariant hit target (docked-mode UX, 2026-07-13): as flex items these
     buttons compressed below their 16px svg under header pressure — icons
     jammed together and read as overlapping before the row clipped. Overflow
     pressure must go to the ellipsizing title, never these buttons. */
  flex: 0 0 auto;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  background: #f5f5f5;
  color: #444;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #eee;
    color: #222;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
  }

  /* Summon toggle ON state (2026-08-07): before this rule NOTHING styled
     data-header-summon-toggle — the button looked identical in both states
     and the QA-reported "orange" had no surviving CSS. Gold accent matches
     the header chrome vocabulary (#C6A75E). Placed after the generic hover
     so the stateful rules win in both source order and specificity. */
  &[data-header-summon-toggle='required'] {
    background: rgba(198, 167, 94, 0.14);
    border-color: var(--brand-gold);
    color: #8a6d2f;
  }

  &[data-header-summon-toggle='required']:hover {
    background: rgba(198, 167, 94, 0.22);
    color: #6f5623;
  }

  &[data-header-summon-error] {
    border-color: rgba(176, 32, 32, 0.45);
    color: #b02020;
  }

  /* Touch: 32px is under every platform's touch-target floor, and 40px — what
     this said until 2026-09-13 — is still under it. 44px is the number both
     platforms actually publish (iOS HIG, Android Material), and it is what the
     composer's own controls were raised to in the same program, so the two
     rails no longer disagree about how big a finger is. */
  @media (hover: none) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

/**
 * Docked header overflow popover (docked density round, 2026-08-01): at
 * docked width the rail keeps 3 invariant buttons max (⋯ / mode-cycle /
 * hide) and the secondary actions (pin, archive, add-people, new-thread,
 * clear) live in this menu. Body-portaled at BODY_PORTAL_Z_INDEX with fixed
 * attrs->style coordinates — the SAME conventions as SidebarAccountMenu
 * (fullscreen-burial lesson, see that component's doc) — but it opens
 * DOWNWARD from the top-right rail and wears the header's LIGHT surface
 * (the sidebar menu is dark because its rail is dark).
 */
export const HeaderOverflowMenu = styled.div.attrs<{ $left: number; $top: number }>((props) => ({
  style: {
    left: `${props.$left}px`,
    top: `${props.$top}px`,
  },
}))<{ $left: number; $top: number }>`
  position: fixed;
  z-index: ${BODY_PORTAL_Z_INDEX};
  min-width: 184px;
  max-width: min(260px, calc(100vw - 16px));
  padding: 6px;
  background: #ffffff;
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(198, 167, 94, 0.1);
`;

export const HeaderOverflowMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  svg {
    flex: none;
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: #222;
    background: rgba(198, 167, 94, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: -2px;
  }
`;

// ============================================================================
// Collapsed/Docked Bar
// ============================================================================

export const DockedCollapsedBar = styled.button`
  position: fixed;
  right: 24px;
  bottom: ${CHAT_LAYOUT.DOCKED_BOTTOM}px;
  width: ${CHAT_LAYOUT.DOCKED_WIDTH};
  padding: 0.9rem 1.1rem;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  background: var(--brand-canvas);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  z-index: ${CHAT_DOCKED_COLLAPSED_Z_INDEX};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-align: left;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.16);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  }

  @media (max-width: 900px) {
    top: auto;
    width: 95%;
    right: 2.5%;
    bottom: 20px;
  }

  @media (max-width: 600px) {
    width: calc(100% - 2rem);
    right: 1rem;
    bottom: 16px;
  }
`;

/**
 * Minimized-pill thread label (docked density round, 2026-08-01): the ONE
 * compact "you are here" marker inside the closed bottom-right docked pill —
 * small, ellipsized, non-interactive (the whole pill opens the panel, so the
 * label never competes with the input/send hit targets). Replaces the
 * full-size chip + "+" pair that crowded the ~420px pill. Gold family, quiet.
 */
export const MinimizedThreadLabel = styled.span`
  flex: 0 1 auto;
  align-self: center;
  min-width: 0;
  max-width: 34%;
  display: inline-block;
  padding: 4px 10px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  color: #8a6d2f;
  background: rgba(198, 167, 94, 0.14);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 999px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
  pointer-events: none;
`;

// ============================================================================
// Drag Overlay
// ============================================================================

export const DragOverlay = styled.div<{ $isDragging: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: ${props => props.$isDragging ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
  border-width: 2px;
  border-style: dashed;
  border-color: var(--brand-gold);
  gap: 8px;

  svg {
    width: 28px;
    height: 28px;
    stroke: var(--brand-gold-hover);
  }

  div {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--brand-gold-hover);
  }
`;

// ============================================================================
// Attachment Components
// ============================================================================

/*
 * AttachmentPreviewPanel
 *
 * Historically rendered as position:absolute anchored to the attach
 * button's parent <div style="position:relative">. That worked fine for
 * 1-3 attachments but suffered from a hard clip at the top of the
 * GlobalChatContainer (which has overflow:hidden for rounded-corner and
 * transition masking) whenever the panel grew tall or the chat window
 * was still transitioning in. On attachment-heavy flows (Miles: 10 files
 * at once) the panel appeared cut off because its bounding box was
 * clipped by the chat container's overflow rect.
 *
 * Fix: when $portaled is true, the panel is rendered into document.body
 * via createPortal and positioned with fixed coordinates derived from
 * the attach button's getBoundingClientRect(). This escapes every
 * ancestor overflow:hidden so the panel can float above the chat
 * container (and over the rest of the page) no matter how many files
 * are attached. When $portaled is false, behavior falls back to the
 * previous position:absolute layout for callers that haven't opted in.
 */
export const AttachmentPreviewPanel = styled.div<{
  isVisible: boolean;
  $portaled?: boolean;
  $anchorRight?: number;
  $anchorBottom?: number;
}>`
  ${props => props.$portaled ? `
    position: fixed;
    /* Anchor the panel's bottom-right corner 12px above the attach button */
    right: ${Math.max(8, (props.$anchorRight ?? 8))}px;
    bottom: ${Math.max(8, (props.$anchorBottom ?? 80))}px;
    /* Body-portaled: must clear the chat container in EVERY mode. A
       bespoke number floated above the docked pane and was buried by the
       fullscreen takeover; the rung is the only correct answer. */
    z-index: ${BODY_PORTAL_Z_INDEX};
  ` : `
    position: absolute;
    bottom: calc(100% + 12px);
    right: 0;
    z-index: 100;
  `}
  width: min(360px, calc(100vw - 48px));
  /*
   * Cap the panel to the available space above the attach button so it
   * never gets cut off at the top of the chat viewport when many files
   * are attached. The panel is flex so the header stays pinned and
   * <PreviewBody> scrolls inside it.
   *
   * Portaled: we have the full viewport to play with, so cap at 60vh.
   * Non-portaled (legacy fallback): keep the older tighter reserve.
   */
  max-height: ${props => props.isVisible
    ? (props.$portaled
      ? 'min(60vh, 520px)'
      : 'min(340px, calc(100vh - 220px))')
    : '0'};
  /* Floor the panel height so it can never collapse to header-only
     when the body has content. On viewports where the panel is content-
     sized (no parent height drives flex distribution), the body's
     flex:1 1 auto + min-height:0 was resolving to 0 and the file row
     mounted invisibly under the header. Regression flagged by Miles
     2026-05-10 23:33 UTC on a 1363x463 viewport. */
  min-height: ${props => props.isVisible ? '92px' : '0'};
  background: white;
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.22), 0 8px 30px rgba(0, 0, 0, 0.12);
  /* overflow:hidden was a contributing factor on the collapsed-body
     case; the body owns its own scroll now. */
  overflow: visible;
  display: flex;
  flex-direction: column;
  transition: ${props => props.$portaled
    ? 'max-height 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease, visibility 0.3s ease'
    : 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'};
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  border: 1px solid rgba(0, 0, 0, 0.1);

  /* Pointer arrow anchored to bottom-right, pointing down toward the attachment button */
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 16px;
    width: 12px;
    height: 12px;
    background: white;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    transform: rotate(45deg);
    opacity: ${props => props.isVisible ? 1 : 0};
    transition: opacity 0.3s ease;
  }

  @media (max-width: 600px) {
    width: calc(100vw - 24px);
    ${props => !props.$portaled ? 'right: -8px;' : ''}
    /* Mobile viewport: tighter reserve since the on-screen keyboard and
       prompt bar both compete for vertical space. */
    max-height: ${props => props.isVisible
      ? (props.$portaled
        ? 'min(55vh, 420px)'
        : 'min(300px, calc(100vh - 180px))')
      : '0'};
  }
`;

export const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: var(--brand-canvas);
  /* Header stays pinned at the top of the flex panel even as the body scrolls. */
  flex: 0 0 auto;
`;

export const PreviewTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

export const PreviewBody = styled.div`
  /* Size to intrinsic content height with a per-body cap so long
     attachment lists scroll internally; flex:0 0 auto is reliable
     when the parent panel has no fixed height (the panel only has a
     min-height + max-height, not a fixed height, so flex:1 1 auto
     here was resolving to 0 and silently hiding file rows). */
  flex: 0 0 auto;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;

  &::-webkit-scrollbar {
    width: 4px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
`;

export const FilePreviewItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: #f5f7fa;
  border-radius: 6px;
  transition: all 0.2s ease;
  position: relative;
  /* Explicit floor so a row can never silently collapse if a child
     element accidentally shrinks (defense in depth alongside the
     panel min-height fix). */
  min-height: 52px;
  gap: 8px;

  &:hover {
    background: #edf2f7;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FilePreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 8px;
  margin-right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FileName = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
`;

export const FileSize = styled.div`
  font-size: 0.75rem;
  color: var(--brand-text-muted);
  white-space: nowrap;
`;

export const RemoveButton = styled.button`
  width: 22px;
  height: 22px;
  padding: 3px;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--brand-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: #f53;
    background: rgba(255, 85, 51, 0.1);
  }
`;

export const ClearAllButton = styled.button`
  font-size: 0.8rem;
  color: #666;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #f53;
    background: rgba(255, 85, 51, 0.05);
  }
`;

export const AddMoreButton = styled.button`
  background: none;
  border: none;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #3182ce;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(49, 130, 206, 0.08);
  }
`;

// ============================================================================
// Message Attachment Components
// ============================================================================

export const AttachmentsContainer = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

export const AttachmentItem = styled.button<{ isUser: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.12), rgba(63, 202, 151, 0.1))' 
    : 'rgba(0, 0, 0, 0.04)'};
  border: 1px solid ${props => props.isUser 
    ? 'rgba(33, 150, 243, 0.25)' 
    : 'rgba(0, 0, 0, 0.08)'};
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  margin-bottom: 0.25rem;

  /* Icon color */
  svg {
    color: ${props => props.isUser ? '#2196f3' : '#64748b'};
    stroke: ${props => props.isUser ? '#2196f3' : '#64748b'};
    flex-shrink: 0;
  }

  &:hover {
    background: ${props => props.isUser 
      ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.18), rgba(63, 202, 151, 0.15))' 
      : 'rgba(0, 0, 0, 0.07)'};
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.isUser ? 'rgba(33, 150, 243, 0.35)' : 'rgba(0, 0, 0, 0.12)'};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const AttachmentName = styled.span`
  white-space: normal;
  word-break: break-word;
  overflow: visible;
  text-overflow: clip;
  flex: 1;
  max-width: none;
  font-weight: 600;
  font-size: 0.82rem;
  color: #1e293b;
  line-height: 1.3;
`;

export const AttachmentSize = styled.span<{ isUser: boolean }>`
  font-size: 0.72rem;
  margin-right: 0.25rem;
  padding-left: 0.5rem;
  color: ${props => props.isUser ? '#2196f3' : '#64748b'};
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0.85;
`;

// ============================================================================
// Special Message Tile Components
// ============================================================================

/**
 * Color palette for special message tile types.
 * Each message type has a distinct border color for visual distinction.
 */
export const SPECIAL_TILE_COLORS = {
  // Brand-consistent (2026-08-12 owner directive): INFORMATIONAL chrome rides
  // the Prest0 warm ink/gold family — only true STATE tiles (alert, warning,
  // error, success) keep semantic hues.
  //
  // 2026-08-19 (owner report, deep-research clarify card screenshot): the
  // research/formatting/progress/system rows were left on their one-off
  // cool hues when the directive landed — the violet left border on every
  // deep-research card came from here. They now ride the same warm family
  // as the rest of the platform chrome; distinction comes from tone within
  // the family, not hue.
  notification: 'var(--brand-text-muted)', // Warm muted ink
  alert: '#DC2626',        // Red (semantic state — kept)
  info: '#8A6D2F',         // Deep gold — the platform accent
  warning: '#F59E0B',      // Amber (semantic state — kept)
  error: '#EF4444',        // Red (semantic state — kept)
  success: '#10B981',      // Green (semantic state — kept)
  system: '#1C1917',       // Warm ink
  research: '#8A6D2F',     // Deep gold (deep research — marquee informational surface)
  formatting: 'var(--brand-gold)',   // Mid gold (formatting)
  progress: '#6B6459',     // Warm subtitle ink (in-progress)
} as const;

/**
 * Slide-up animation for special message tiles.
 */
export const specialTileSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Main container for special message tiles.
 * Features a left border accent based on message type.
 */
export const SpecialTileContainer = styled.div<{
  $messageType?: string;
  $priority?: string;
  $animate?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  ${props => props.$animate !== false && css`
    animation: ${specialTileSlideUp} 200ms ease-out;
  `}
  border-left: 3px solid ${props => SPECIAL_TILE_COLORS[props.$messageType as keyof typeof SPECIAL_TILE_COLORS] || SPECIAL_TILE_COLORS.info};

  /* Chat tables (2026-08-02) — same contract as the Message bubble: cells keep
     words whole, the table scrolls horizontally inside .chat-table-scroll at
     narrow widths instead of crushing to letter-soup columns. */
  .chat-table-scroll {
    overflow-x: auto;
    max-width: 100%;
    margin: 0.6em 0;
    -webkit-overflow-scrolling: touch;
  }
  table {
    border-collapse: collapse;
    width: max-content;
    max-width: none;
    font-size: 0.86em;
    line-height: 1.35;
    white-space: normal;
  }
  th, td {
    border: 1px solid rgba(15, 23, 42, 0.16);
    padding: 5px 9px;
    text-align: left;
    vertical-align: top;
    word-break: normal;
    overflow-wrap: normal;
    white-space: normal;
    max-width: 30ch;
  }
  th {
    background: rgba(15, 23, 42, 0.05);
    font-weight: 600;
    white-space: nowrap;
  }

  /* Urgent priority gets subtle background tint */
  ${props => props.$priority === 'urgent' && css`
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.03) 0%, white 100%);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.1);
  `}

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

/**
 * Header row with icon, title, and optional timestamp.
 */
export const SpecialTileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

/**
 * Icon container for the message type icon.
 */
export const SpecialTileIcon = styled.div<{ $messageType?: string }>`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => {
    const color = SPECIAL_TILE_COLORS[props.$messageType as keyof typeof SPECIAL_TILE_COLORS] || SPECIAL_TILE_COLORS.info;
    return `${color}15`; // 15% opacity background
  }};
  color: ${props => SPECIAL_TILE_COLORS[props.$messageType as keyof typeof SPECIAL_TILE_COLORS] || SPECIAL_TILE_COLORS.info};

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`;

/**
 * Title text for the message.
 */
export const SpecialTileTitle = styled.div<{ $messageType?: string }>`
  flex: 1;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${props => SPECIAL_TILE_COLORS[props.$messageType as keyof typeof SPECIAL_TILE_COLORS] || '#1f2937'};
  line-height: 1.4;
`;

/**
 * Subtitle/description text.
 */
export const SpecialTileSubtitle = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
  margin-top: -2px;
`;

/**
 * Main content area for message body.
 */
export const SpecialTileContent = styled.div`
  flex: 1;
  min-height: 0;
  color: #1f2937;
  font-size: 0.9rem;
  line-height: 1.45;

  a {
    color: #1f2937;
    text-decoration: underline;
    &:hover {
      color: #111827;
    }
  }

  .mention {
    background: #f3f4f6;
    padding: 0 4px;
    border-radius: 4px;
    font-weight: 500;
  }

  /* Markdown styling */
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong, b {
    font-weight: 600;
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.85em;
    font-family: 'SF Mono', 'Menlo', monospace;
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }
`;

/**
 * Container for action buttons.
 * Uses CSS grid for equal-width buttons.
 */
export const SpecialTileActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 12px;
`;

/**
 * Action button within special message tiles.
 */
export const SpecialTileActionButton = styled.button<{
  $style?: string;
  $loading?: boolean;
  $selected?: boolean;
  $unselected?: boolean;
}>`
  padding: 11px 16px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;

  ${props => {
    switch (props.$style) {
      case 'primary':
        return css`
          background: #111827;
          border: none;
          color: white;
          box-shadow: 0 12px 28px rgba(17, 24, 39, 0.28);
          &:hover:not(:disabled) {
            transform: scale(1.02);
            box-shadow: 0 14px 32px rgba(17, 24, 39, 0.35);
          }
        `;
      case 'destructive':
        return css`
          background: #dc2626;
          border: none;
          color: white;
          box-shadow: 0 12px 28px rgba(220, 38, 38, 0.28);
          &:hover:not(:disabled) {
            background: #b91c1c;
            transform: scale(1.02);
            box-shadow: 0 14px 32px rgba(220, 38, 38, 0.35);
          }
        `;
      case 'link':
        return css`
          background: transparent;
          border: 1px solid transparent;
          color: #3b82f6;
          text-decoration: underline;
          &:hover:not(:disabled) {
            color: #2563eb;
            transform: scale(1.02);
          }
        `;
      default: // secondary
        return css`
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          color: #475569;
          &:hover:not(:disabled) {
            background: #e2e8f0;
            border-color: #94a3b8;
            transform: scale(1.02);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => props.$loading && css`
    pointer-events: none;
    &::after {
      content: '';
      width: 14px;
      height: 14px;
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      animation: ${pendingSpin} 0.8s linear infinite;
      margin-left: 6px;
    }
  `}

  /* Selected state - green with checkmark to indicate user's choice */
  ${props => props.$selected && css`
    background: #059669 !important;
    border-color: #059669 !important;
    color: white !important;
    box-shadow: 0 0 0 2px #10b981, 0 12px 28px rgba(5, 150, 105, 0.35) !important;
    cursor: default;

    &::before {
      content: '\\2713';
      margin-right: 8px;
      font-weight: bold;
    }

    &:hover {
      transform: none;
    }
  `}

  /* Unselected/greyed out state when another button was selected */
  ${props => props.$unselected && css`
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
    filter: grayscale(50%);
  `}

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

/**
 * Timestamp display for special tiles.
 */
export const SpecialTileTimestamp = styled.span`
  font-size: 0.7rem;
  color: #9ca3af;
  margin-left: auto;
  white-space: nowrap;
`;

/**
 * Loading spinner overlay for streaming state.
 */
export const SpecialTileStreamingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6366f1;
  font-size: 0.82rem;
  animation: ${streamingPulse} 1.5s ease-in-out infinite;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: ${streamingPulse} 1.5s ease-in-out infinite;
  }
`;

/**
 * Footer row for special tiles containing timestamp, copy button, and info button.
 * Similar to MessageTime but for SpecialMessageTile.
 */
export const SpecialTileFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
`;

/**
 * Processing time indicator for special tiles (compute time from AI).
 */
export const SpecialTileProcessingTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #94a3b8;
  font-size: 0.7rem;

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`;

// ============================================================================
// Low Balance Warning Components
// ============================================================================

/**
 * Animation for the low balance warning dropup.
 */
export const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Container for the low balance warning that appears above the prompt bar.
 * Supports two visual states: warning (amber) and critical (red).
 */
export const LowBalanceOverlay = styled.div<{ $visible: boolean; $isCritical?: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 12px;
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${props => props.$isCritical ? '#EF4444' : '#F59E0B'};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 16px 20px;
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  gap: 14px;
  z-index: 10;
  animation: ${slideUp} 0.25s ease-out;
  max-width: 380px;
  width: max-content;

  @media (max-width: 480px) {
    max-width: calc(100vw - 40px);
    padding: 14px 16px;
    gap: 12px;
  }
`;

/**
 * Icon container with gradient background.
 * Amber for warning, red for critical.
 */
export const LowBalanceIcon = styled.div<{ $isCritical?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.$isCritical
    ? 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'
    : 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
    color: ${props => props.$isCritical ? '#DC2626' : '#D97706'};
  }
`;

/**
 * Content wrapper for message text and button.
 */
export const LowBalanceContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/**
 * Personable warning message text.
 */
export const LowBalanceText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.45;
  font-weight: 400;
`;

/**
 * CTA button to navigate to wallet.
 * Amber for warning state, red for critical state.
 */
export const LowBalanceButton = styled.button<{ $isCritical?: boolean }>`
  background: ${props => props.$isCritical
    ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
    : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  align-self: flex-start;
  box-shadow: ${props => props.$isCritical
    ? '0 2px 8px rgba(239, 68, 68, 0.3)'
    : '0 2px 8px rgba(245, 158, 11, 0.3)'};

  &:hover {
    background: ${props => props.$isCritical
      ? 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
      : 'linear-gradient(135deg, #D97706 0%, #B45309 100%)'};
    transform: translateY(-1px);
    box-shadow: ${props => props.$isCritical
      ? '0 4px 12px rgba(239, 68, 68, 0.4)'
      : '0 4px 12px rgba(245, 158, 11, 0.4)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

/**
 * Wrapper for prompt bar when input is disabled due to low balance.
 * Adds visual indication that input is blocked.
 */
export const DisabledPromptWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.5);
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
  }
`;


// ============================================================================
// Thread Switcher Bar (Slack-style) — lives at the bottom of the chat area,
// below the thinking/status bubble's own row (PendingOverlayRow).
// Gated to account-specific Prest0n instances only (see GlobalChatSystem.tsx
// isInternalPrest0n guard and config.enableThreadSwitcher).
// ============================================================================

const threadChipSpin = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * Outer container — rendered in normal document flow between ChatMessages
 * and ChatFooter, so it never overlaps messages. Left-aligned Slack-style
 * tab bar with a subtle top divider + matching chat background.
 */
export const ThreadSwitcherContainer = styled.div<{ $docked?: boolean }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 8px 12px;
  background: var(--brand-canvas); /* matches ChatMessages background */
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  z-index: 4;

  @media (max-width: 700px) {
    padding: 5px 8px 7px 8px;
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    padding: 5px 8px 7px 8px;
  }

  /* Docked density (owner feedback round, 2026-08-01): the strip crowded
     directly against the composer in the docked panel. The docked strip is
     a COMPACT single row — smaller chips, tighter rail — with deliberate
     air below it so the composer's own top border reads as a clear
     separation. Density flows through the --thread-strip-* variables the
     chip components consume (same variable-rhythm convention as
     --chat-header-inset), so the strip's children need no mode props and
     every other mode keeps the wide defaults. The strip stays flex: 0 0
     auto — it can never steal composer height in any mode.

     Round 2 (owner screenshot, 2026-08-02): chips are busier now — the
     uniform spinner + elapsed clock rides EVERY processing chip (2026-08-01
     owner override) — and the rail still read tight against the composer.
     Re-tune, don't shrink uniformly: chip padding and the in-chip gap
     tighten one step (the clock earns its room inside the chip; the 30px
     min-height hit target is invariant), and the container's BOTTOM padding
     grows 9px → 14px so the strip stands clearly off the composer's top
     border — deliberate vertical rhythm (air), not a second divider line. */
  ${props => props.$docked && css`
    padding: 3px 10px 14px 10px;
    --thread-strip-scroll-padding: 4px 2px 6px 2px;
    --thread-strip-scroll-gap: 8px;
    --thread-strip-chip-min-height: 30px;
    --thread-strip-chip-padding: 4px 10px;
    --thread-strip-chip-gap: 6px;
    --thread-strip-chip-font: 12px;
    --thread-strip-label-max: 110px;
    --thread-strip-new-padding: 6px 12px;
    --thread-strip-new-font: 15px;
  `}
`;

/**
 * Unified bottom action rail — thread tabs ONLY (phantom-chip fix, Miles
 * 2026-07-13). The thinking/status widget used to share this row with the
 * chips; its pill shape read as a phantom third thread, so it moved into
 * its own full-width `PendingOverlayRow` above this rail. The fallback
 * "working" state now renders INSIDE the active chip (spinner + elapsed).
 *
 * Sizing (Miles 2026-05-10): the rail must FILL the available row width
 * so its inner `ThreadSwitcherScroll` (flex: 1 1 auto, overflow-x: auto)
 * has a finite width to scroll inside. Previously the rail was
 * `flex: 0 0 auto` which let it grow to its content size — chip
 * overflow then pushed the trailing `ThreadNewButton` ("+") right off
 * the visible composer area, with no horizontal scroll because the
 * rail itself never reached its container's edge. Result: with many
 * parallel threads the user could not see, or click, the "+" button
 * unless they happened to know it was off-screen and could trigger a
 * horizontal page scroll.
 *
 * `flex: 1 1 auto; min-width: 0; overflow: hidden` constrains the
 * rail to the parent row, the inner scroller hits its own overflow
 * boundary at chip count N, and chips scroll horizontally inside
 * `ThreadSwitcherScroll` while `ThreadNewButton` (flex-shrink: 0,
 * sibling of the scroll inside the bar) stays anchored at the right
 * edge.
 */
export const ThreadSwitcherActionRail = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  /* T4 hotfix: anchors the ABSOLUTE overflow indicators. They must overlay
     the strip rather than sit in flex flow — as flex siblings, mounting one
     shrank the scroll viewport, which changed the hidden-chip count, which
     could unmount it again: a render oscillation that ate the tab's memory
     (prod OOM, 2026-07-12). Absolute overlays have zero layout feedback. */
  position: relative;
  /* Hide any pixel that would visually escape this row. The inner
     scroll rail's own overflow-x: auto handles the chip overflow,
     so this overflow: hidden is purely a backstop so a momentary
     layout glitch cannot push a chip past the action rail's edge
     and obscure the sibling new-thread button. */
  overflow: hidden;

  /* W4 chip-scale .pending-widget-wrapper overrides removed 2026-07-13:
     no pending widget renders inside the rail anymore (see
     PendingOverlayRow + the in-chip ThreadChipTurnIndicator). */
`;


/**
 * Horizontal scroll rail that holds the chips. Hidden scrollbar, thin
 * momentum-friendly behavior on touch.
 */
export const ThreadSwitcherScroll = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  /* Docked density (2026-08-01): rail metrics read the --thread-strip-*
     variables ThreadSwitcherContainer sets in its $docked branch; the
     fallbacks are the unchanged wide-mode values. */
  gap: var(--thread-strip-scroll-gap, 10px);
  overflow-x: auto;
  /* overflow-y: visible so the active/hover chip's glow + box-shadow are
     not vertically clipped by the scroll rail. Horizontal scroll still
     works; vertical expansion has room because the parent action rail
     is flex with align-items: center. */
  overflow-y: visible;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: var(--thread-strip-scroll-padding, 6px 4px 10px 4px);

  /* Hide scrollbar, content still scrollable. */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    height: 0;
    background: transparent;
  }
`;

/**
 * Individual thread chip. ACTIVE-thread treatment (owner directive, Miles
 * 2026-07-14: "more clear what thread the user is currently in… not just a
 * little more highlighted"): the active chip is DECISIVELY marked with a
 * filled gold-tinted background + stronger gold border + bolder darker title
 * (see ThreadChipLabel), while inactive chips keep a subtle outline. Same
 * fonts/sizes/geometry — the distinction is fill/contrast/weight only, and
 * it stays legible while the uniform in-chip turn indicator spins. The old
 * ambient shimmer/glow animation on the active chip is gone: with every
 * processing chip now showing a spinner, an always-animating active chip
 * read as false activity. Mirrors ThreadSidebarRow so both surfaces mark
 * "you are here" the same way. Palette: the existing #B6924C/#C6A75E gold
 * family.
 */
export const ThreadChipButton = styled.button<{ $active?: boolean }>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  /* Docked density (2026-08-01): chip metrics read the --thread-strip-*
     variables ThreadSwitcherContainer sets in its $docked branch. Fallbacks
     are the unchanged wide-mode values, so the floating pill / wide strip
     render exactly as before. */
  gap: var(--thread-strip-chip-gap, 12px);
  max-width: 220px;
  min-height: var(--thread-strip-chip-min-height, 40px);
  padding: var(--thread-strip-chip-padding, 10px 18px);

  /*
   * Stacking:
   *   - default chips sit at z-index 1 so their shadow overlaps the
   *     rail background cleanly.
   *   - the active chip sits at z-index 3 so its gold glow + shadow
   *     render in front of adjacent chips (otherwise the neighbor
   *     appears to "clip" the active chip's edges when the switcher
   *     scroll is full).
   *   - on hover/focus we bump to z-index 4 so the chip lifts cleanly
   *     over siblings during the transform: translateY(-1px) hover.
   */
  z-index: ${props => props.$active ? 3 : 1};

  /* Reveal the close affordance on hover / keyboard focus. */
  &:hover [data-thread-close-btn],
  &:focus-within [data-thread-close-btn] {
    display: inline-flex;
  }

  /* T4: one-time completion pulse — fired when this thread's processing just
     finished; the chip pulses IN PLACE (ordering is stable) so the eye finds
     the finished work where it always was. */
  @keyframes chipCompletedPulse {
    0% { box-shadow: 0 0 0 0 rgba(198, 167, 94, 0.55); }
    100% { box-shadow: 0 0 0 10px rgba(198, 167, 94, 0); }
  }
  &[data-completed-pulse='true'] {
    animation: chipCompletedPulse 0.85s ease-out 3;
  }

  /* T1: reveal the pin affordance the same way; a PINNED chip's marker is
     always visible (data-pinned) so pin state reads at a glance. */
  &:hover [data-thread-pin-btn],
  &:focus-within [data-thread-pin-btn],
  [data-thread-pin-btn][data-pinned='true'] {
    display: inline-flex;
  }

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: var(--thread-strip-chip-font, 13px);
  font-weight: ${props => props.$active ? 600 : 500};
  line-height: 1;
  /* Both golds must read on the chip's cream ground (#F1EBDC): the inactive
     one measured 2.45:1 there and the active one 4.10 — and the chat strip
     renders on every page, so this single line was six of the audit's rows.
     --brand-gold-text clears it at 4.95; the active state stays one step
     stronger by WEIGHT and by charcoal, not by a lighter gold. */
  color: ${props => props.$active ? 'var(--brand-charcoal)' : 'var(--brand-gold-text)'};
  letter-spacing: 0.01em;
  /* ACTIVE: filled gold-tint (solid, unmistakable). INACTIVE: the quiet
     near-white gradient + subtle outline. Fill/contrast only — no new
     geometry, no size change (docked 320px width math untouched). */
  background: ${props => props.$active
    ? 'rgba(198, 167, 94, 0.18)'
    : 'linear-gradient(135deg, var(--brand-canvas) 0%, #f8f6ef 50%, var(--brand-canvas) 100%)'};
  border: 1px solid ${props => props.$active ? 'rgba(182, 146, 76, 0.75)' : 'rgba(198, 167, 94, 0.18)'};
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: ${props => props.$active
    ? '0 2px 10px rgba(198, 167, 94, 0.22)'
    : 'none'};
  user-select: none;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(198, 167, 94, 0.35);
    box-shadow: 0 6px 18px rgba(198, 167, 94, 0.28), 0 0 0 1px rgba(198, 167, 94, 0.18);
    z-index: 4;
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 2px;
    z-index: 4;
  }

  &:active {
    transform: translateY(0);
  }

  /* Touch devices have no hover: the close/pin affordances must be always
     visible or they simply don't exist (mobile UX 2026-08-14). Chips also
     grow to the 44px touch-target floor. */
  @media (hover: none) {
    min-height: max(var(--thread-strip-chip-min-height, 40px), 44px);

    [data-thread-close-btn],
    [data-thread-pin-btn] {
      display: inline-flex;
    }
  }
`;

/**
 * Chip label — truncates with ellipsis so long project titles don't
 * blow out the chip width. Active-thread treatment (Miles 2026-07-14):
 * bolder, darker gold title on the active chip's filled background —
 * same font/size, weight/contrast only.
 */
export const ThreadChipLabel = styled.span<{ $active?: boolean }>`
  display: inline-block;
  /* Docked density (2026-08-01): tighter label cap in the docked strip via
     the container-set variable; wide default unchanged. */
  max-width: var(--thread-strip-label-max, 140px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Both golds must read on the chip's cream ground (#F1EBDC): the inactive
     one measured 2.45:1 there and the active one 4.10 — and the chat strip
     renders on every page, so this single line was six of the audit's rows.
     --brand-gold-text clears it at 4.95; the active state stays one step
     stronger by WEIGHT and by charcoal, not by a lighter gold. */
  color: ${props => props.$active ? 'var(--brand-charcoal)' : 'var(--brand-gold-text)'};
  font-weight: ${props => props.$active ? 600 : 500};
`;

/**
 * Small spinner rendered inside the chip when Prest0n is processing
 * in that thread. Uses the same gold accent as PendingWidget.
 */
export const ThreadChipSpinner = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(198, 167, 94, 0.2);
  border-top-color: var(--brand-gold);
  border-radius: 50%;
  animation: ${threadChipSpin} 0.8s linear infinite;
`;

/**
 * In-chip fallback turn indicator (phantom-chip fix, Miles 2026-07-13):
 * small spinner + ticking compact elapsed clock (0:07) rendered INSIDE the
 * ACTIVE chip / sidebar row — after the title — when the active thread's
 * turn is running but NO pane indicator is visible. Replaces the rail-level
 * "working" pill that sat beside the chips and read as a phantom third
 * thread. Background chips keep their plain leading spinner.
 */
export const ThreadChipTurnIndicator = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: var(--brand-gold-hover);
  font-variant-numeric: tabular-nums;

  ${ThreadChipSpinner} {
    width: 12px;
    height: 12px;
    border-width: 2px;
  }
`;

/**
 * ThreadUnreadBadge — the ONE unread marker every thread surface wears
 * (per-thread notification badges, 2026-09-08).
 *
 * Two shapes, one colour: a COUNT (unreadCount > 0) or a DOT (`$dot` — the
 * server-driven "someone else was active since you last looked" predicate,
 * hasUnseenActivity in useChatThreads, with no local count yet). Both are
 * pure decoration: aria-hidden, pointer-events none, never focusable — the
 * count is folded into the row's own aria-label by the renderer.
 *
 * PLACEMENT is the load-bearing part. The old badge was one absolute box at
 * top:-4px/right:-4px for every surface — which worked on the chip strip
 * (chips are position:relative) and NEVER rendered anywhere else: the rail
 * rows and sheet rows had no positioned ancestor, so the badge resolved
 * against the rail (overflow:hidden → clipped) or the fixed sheet (stacked on
 * its corner), and in fullscreen an escaped badge lands UNDER the pinned
 * banner band. Now:
 *   • 'overhang' — the chip strip keeps its Slack-style corner overhang;
 *   • 'inset'    — rail / sheet / panel rows: inside the row, never negative
 *                  (ThreadSidebarRow is position:relative for exactly this);
 *   • 'corner'   — header icon buttons (bell / ⋯) and the ☰ trigger: a small
 *                  dot tucked into the button's own corner.
 * No z-index beyond the row's own stacking: a badge is NEVER raised above
 * BANNER_BAND_PINNED_Z_INDEX or BODY_PORTAL_Z_INDEX.
 */
export type ThreadUnreadBadgePlacement = 'overhang' | 'inset' | 'corner';

export const ThreadUnreadBadge = styled.span<{ $dot?: boolean; $placement?: ThreadUnreadBadgePlacement }>`
  ${props => props.$placement === 'inset'
    ? css`
      /* Row badge (2026-09-09, Miles: "a tad larger… noticeable but not
         covering other elements"): a flex item in the row's OWN flow after
         the title block, vertically centred — the title truncates beside it,
         the pin / × sit after it, and on touch (where those buttons are
         always visible) nothing is ever covered. */
      position: static;
      flex: 0 0 auto;
      align-self: center;
      margin-left: 4px;
    `
    : props.$placement === 'corner'
      ? css`position: absolute; top: 3px; right: 3px;`
      : css`position: absolute; top: -4px; right: -4px;`}
  ${props => props.$dot
    ? css`
      width: 10px;
      height: 10px;
      min-width: 0;
      padding: 0;
      border-radius: 50%;
    `
    : css`
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      border-radius: 12px;
    `}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-danger);
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1;
  border: 2px solid var(--brand-canvas);
  box-shadow: 0 1px 3px rgba(226, 74, 74, 0.4);
  pointer-events: none;
  user-select: none;
  letter-spacing: 0;
  z-index: 1;

  /* Touch: a step larger still — read at arm's length, under a thumb. */
  @media (pointer: coarse) {
    ${props => props.$dot
      ? css`
        width: 12px;
        height: 12px;
      `
      : css`
        min-width: 22px;
        height: 22px;
        font-size: 0.8rem;
      `}
  }
`;

/**
 * Legacy name — the chip strip's original badge. Kept as an ALIAS of the one
 * badge so the existing chip-strip pins (dockedFooterDensity) and any
 * out-of-tree import keep working; new code uses ThreadUnreadBadge.
 */
export const ThreadChipUnreadBadge = ThreadUnreadBadge;

/**
 * Small close (×) button tucked inside a chip. Hidden by default; reveals
 * only on hover / keyboard focus of the parent chip so it can never be
 * mistaken for an always-on delete. Clicking it opens a confirmation modal
 * (see GlobalChatSystem.tsx `showDeleteThreadConfirm`) — NOT a one-click
 * delete, matching the "don't make it easy to delete" requirement.
 */
/**
 * T1 (thread-workflow UX): pin toggle inside a chip. Hidden until the chip is
 * hovered/focused (like the ×), EXCEPT when the thread is pinned — then the
 * gold marker stays visible so pin state reads at a glance. Same hit-target
 * economics as the close button.
 */
export const ThreadChipPinButton = styled.button<{ $pinned?: boolean }>`
  flex-shrink: 0;
  display: ${props => (props.$pinned ? 'inline-flex' : 'none')};
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;
  margin-left: 6px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  line-height: 0;
  color: ${props => (props.$pinned ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.45)')};

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.12);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 2px;
    display: inline-flex;
  }

  svg {
    width: 10px;
    height: 10px;
    display: block;
  }

  /* Touch: always visible (the chip reveals it) and big enough to hit. */
  @media (hover: none) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    border-radius: 999px;
    color: ${props => (props.$pinned ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.55)')};

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

/**
 * Archive (×) button context contract (round 3, 2026-08-01): the base
 * styling below is tuned for the LIGHT chip strip (#FAFAF8), where the tiny
 * dark square reads clearly and is unchanged. On the DARK sidebar rail that
 * same near-black square was invisible at rest and read near-black on hover —
 * so every dark-background render passes `$onDark`, which swaps in a
 * gold-toned × consistent with ThreadChipPinButton (transparent base, gold
 * stroke, brighter gold + subtle background pill on hover). Hover-reveal
 * mechanics (display none → row/chip :hover/:focus-within) are identical in
 * both contexts.
 */
export const ThreadChipCloseButton = styled.button<{ $onDark?: boolean }>`
  flex-shrink: 0;
  display: none;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  min-width: 8px;
  min-height: 8px;
  max-width: 8px;
  max-height: 8px;
  margin-left: 6px;
  padding: 0;
  background: var(--brand-black);
  color: var(--brand-black);
  border: none;
  border-radius: 1px;
  cursor: pointer;
  transition: background 0.15s ease;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  line-height: 0;

  &:hover {
    background: #333;
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 2px;
    display: inline-flex;
  }

  &:active {
    transform: scale(0.92);
  }

  svg {
    display: none;
  }

  /* Touch: the 8px hover-reveal square is untappable — swap to a visible
     stroked × at a real hit size on the light strip. (Placed before the
     $onDark block so the dark-rail variant still wins its own geometry;
     50% not 999px — sidebarUxRound3 pins 999px as the $onDark signature.) */
  @media (hover: none) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    max-width: 24px;
    max-height: 24px;
    background: transparent;
    color: rgba(26, 26, 26, 0.55);
    border-radius: 50%;

    svg {
      display: block;
      width: 12px;
      height: 12px;
      stroke: currentColor;
      stroke-width: 1.6;
    }
  }

  ${props => props.$onDark && css`
    /* Dark-rail variant: same geometry family as ThreadChipPinButton (14px
       hit target, visible stroked ×) so pin and archive read as one set. */
    width: 14px;
    height: 14px;
    min-width: 14px;
    min-height: 14px;
    max-width: 14px;
    max-height: 14px;
    background: transparent;
    color: rgba(198, 167, 94, 0.75);
    border-radius: 999px;

    &:hover {
      color: var(--brand-gold);
      background: rgba(198, 167, 94, 0.16);
    }

    svg {
      display: block;
      width: 10px;
      height: 10px;
      stroke: currentColor;
      stroke-width: 1.4;
    }
  `}
`;

/**
 * Hover/focus tooltip that surfaces a thread's expanded topicSummary on
 * the ThreadSwitcherBar chip. We deliberately do not animate with
 * translateY here so it can't accidentally collide with the chip-lift
 * hover transform.
 *
 * Body-portaled + fixed (docked-mode UX round, Miles 2026-07-13): the old
 * CSS-only variant sat absolutely INSIDE the chip, above the scroll rail's
 * box — and was clipped into invisibility by construction. `overflow-x:
 * auto` on the rail forces computed overflow-y to auto (the old
 * "overflow-y: visible" comment was a CSS misconception — visible computes
 * to auto when the other axis scrolls), and the action rail itself is
 * overflow: hidden, so users only ever saw the native `title` fallback. Do
 * NOT try to fix that with overflow tweaks on the rail — that reintroduces
 * the T4 clipping/oscillation trade-offs documented on
 * ThreadSwitcherActionRail. ThreadSwitcherBar now renders this through a
 * document.body portal at BODY_PORTAL_Z_INDEX with viewport-clamped fixed
 * coordinates (passed via attrs->style so continuous mouse positions don't
 * mint a styled-components class per pixel).
 *
 * Accessibility: the visible tooltip mirrors a `title` attribute on the
 * chip so screen readers and keyboard users still get the content even
 * when this CSS layer is unavailable. The tooltip element itself is
 * `aria-hidden` because it would duplicate the `title` for AT users.
 */
export const ThreadChipTooltip = styled.span.attrs<{ $x: number; $bottom: number }>((props) => ({
  style: {
    left: `${props.$x}px`,
    bottom: `${props.$bottom}px`,
  },
}))<{ $x: number; $bottom: number }>`
  display: block;
  position: fixed;
  transform: translateX(-50%);
  z-index: ${BODY_PORTAL_Z_INDEX};

  /* Never wider than the viewport, even at 320px panes/screens. */
  max-width: min(280px, calc(100vw - 16px));
  min-width: 140px;
  width: max-content;
  padding: 8px 12px;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  color: #2a2418;
  letter-spacing: 0.005em;
  white-space: normal;
  text-align: left;

  background: linear-gradient(135deg, var(--brand-canvas) 0%, #f5f0e4 50%, var(--brand-canvas) 100%);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(40, 30, 10, 0.18), 0 0 0 1px rgba(198, 167, 94, 0.15);
  pointer-events: none; /* never steals hover from the chip itself */

  &::after {
    /* Small downward-pointing notch tying the popover visually to the chip. */
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(198, 167, 94, 0.35);
  }
`;

/**
 * Second line of the chip tooltip — relative activity time + message count,
 * the same meta the sidebar rows show (navigability pass 2026-07-13), so
 * compact views are equally scannable.
 */
export const ThreadChipTooltipMeta = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: rgba(182, 146, 76, 0.75);
`;

/**
 * "+" button to start a new thread/project. Always pinned to the right
 * of the scroll rail so the create-new affordance is always reachable
 * even with many parallel projects.
 *
 * Anchoring (Miles 2026-05-10): `flex: 0 0 auto` plus an explicit
 * `flex-shrink: 0` keep the button at its natural pill width even
 * when the row is tight and the inner `ThreadSwitcherScroll` is at
 * full overflow. The button is a sibling of `ThreadSwitcherScroll`
 * inside a flex `ThreadSwitcherActionRail` (now `flex: 1 1 auto` +
 * `overflow: hidden`). With the rail width-constrained and the inner
 * scroller flex-1, chip overflow stays inside the scroller while the
 * "+" remains anchored at the rail's right edge. Previously the rail
 * was `flex: 0 0 auto` and chips could push "+" past the visible
 * composer width. The combined parent-rail + child-button rule
 * locks the affordance in place even if a future markup change
 * adds another sibling to the row.
 */
export const ThreadNewButton = styled.button`
  flex: 0 0 auto;
  flex-shrink: 0;
  /* Docked density (2026-08-01): the 40px minimum hit target is INVARIANT
     in every density (sticky-right pin, tests/hooks/
     threadNewButtonStickyRight.test.ts + "comfortable hit targets"
     directive) — only padding/font compact via the container-set
     --thread-strip-* variables. */
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--thread-strip-new-padding, 10px 16px);
  background: linear-gradient(135deg, var(--brand-canvas) 0%, #f8f6ef 50%, var(--brand-canvas) 100%);
  color: var(--brand-gold-text);
  border: 1px solid rgba(198, 167, 94, 0.12);
  border-radius: 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: var(--thread-strip-new-font, 17px);
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: none;
  user-select: none;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(198, 167, 94, 0.25);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0);
  }
`;

// ============================================================================
// Mobile thread-list bottom sheet (mobile UX round, 2026-08-14)
//
// On a phone the horizontal chip strip can only show ~2 thread names; the
// sheet is the vertical, finger-sized answer — the SAME rows as the
// fullscreen sidebar (dark rail palette, ThreadSidebarRow anatomy), driven
// by the SAME orderThreads and callbacks, portaled over the fullscreen
// takeover at BODY_PORTAL_Z_INDEX (the styles.ts:380s doctrine).
// ============================================================================

export const ThreadSheetScrim = styled.div<{ $transparent?: boolean }>`
  position: fixed;
  inset: 0;
  /* Docked thread-list round (2026-08-24): the docked panel form is a
     dropdown, not a takeover — its click-away layer stays invisible (no
     page dim), exactly like every other menu's outside-click surface. */
  background: ${props => props.$transparent ? 'transparent' : 'rgba(0, 0, 0, 0.45)'};
  z-index: ${BODY_PORTAL_Z_INDEX};
  animation: ${props => props.$transparent ? 'none' : 'threadSheetScrimIn 0.18s ease-out'};

  @keyframes threadSheetScrimIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ThreadSheetContainer = styled.div<{ $variant?: 'sheet' | 'panel' }>`
  position: fixed;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  flex-direction: column;
  background: rgba(20, 20, 20, 0.98);

  ${props => props.$variant === 'panel'
    /* Docked thread-list round (2026-08-24): the SAME rows/data as the
       mobile sheet, worn as an anchored drop-UP above the docked strip's ☰
       trigger — the SidebarAccountMenu family look (dark surface, gold
       hairline + ring, 12px radius, opens upward from a bottom rail).
       right/bottom coordinates arrive per-open via inline style (measured
       from the trigger, the HeaderOverflowMenu idiom) — the values here are
       fallbacks matching the docked pane's inset. */
    ? css`
      left: auto;
      right: 32px;
      bottom: 140px;
      width: min(420px, calc(100vw - 48px));
      max-height: min(52vh, 440px);
      border: 1px solid rgba(198, 167, 94, 0.35);
      border-radius: 12px;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(198, 167, 94, 0.12);
      padding-bottom: 6px;
      animation: threadPanelRise 0.18s cubic-bezier(0.32, 0.72, 0.24, 1);

      @keyframes threadPanelRise {
        from { transform: translateY(8px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `
    : css`
      left: 0;
      right: 0;
      bottom: 0;
      max-height: 70vh;
      max-height: 70dvh;
      border-top: 1px solid rgba(198, 167, 94, 0.28);
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -18px 50px rgba(0, 0, 0, 0.35);
      /* The sheet owns the bottom edge — pad past the home indicator. */
      padding-bottom: env(safe-area-inset-bottom, 0px);
      animation: threadSheetSlideUp 0.22s cubic-bezier(0.32, 0.72, 0.24, 1);

      @keyframes threadSheetSlideUp {
        from { transform: translateY(24px); opacity: 0.6; }
        to { transform: translateY(0); opacity: 1; }
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Grabber bar — the visual "this is a sheet" affordance. */
export const ThreadSheetHandle = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: rgba(198, 167, 94, 0.4);
  margin: 10px auto 4px;
`;

export const ThreadSheetHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 16px 10px;
`;

export const ThreadSheetTitle = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--brand-gold-hover);
`;

/**
 * The mentions tab's body, in BOTH thread-list renderers (2026-09-17).
 *
 * It stands where ThreadSidebarList / ThreadSheetList stand and must carry the
 * same flex behaviour, or the rail's bottom-pinned footer comes unpinned the
 * moment the tab changes. It scrolls nothing itself: MentionsList scrolls its
 * own list, so the people/all filter and "mark all read" stay put while the
 * mentions under them move.
 *
 * Lives here rather than in either surface because the two are renderers of one
 * data source — a body one of them styles for itself is how they start looking
 * like different products.
 */
export const ThreadListPanelHost = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/** The quiet one-liner a body shows in place of rows. A fact, not an apology. */
export const ThreadListEmptyNote = styled.div`
  padding: 18px 14px;
  color: rgba(255, 255, 255, 0.45);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
  line-height: 1.5;
  text-align: center;
`;

export const ThreadSheetList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 8px 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(198, 167, 94, 0.25) transparent;
`;

/**
 * Strip-side trigger that opens the sheet — host-rendered NEXT TO the chip
 * strip (mobile only), so ThreadSwitcherBar's own pinned DOM stays
 * untouched. Same 40px invariant hit target + gold pill family as
 * ThreadNewButton.
 */
export const ThreadSheetTrigger = styled.button`
  /* Anchor for the roll-up unread count (per-thread badges, 2026-09-08). */
  position: relative;
  flex: 0 0 auto;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  background: linear-gradient(135deg, var(--brand-canvas) 0%, #f8f6ef 50%, var(--brand-canvas) 100%);
  /* On the canvas gradient this is 2.7:1, and on a phone it is now the
     header's thread-list control (2026-09-13). --brand-gold-text keeps the
     gold reading at 5.4:1. */
  color: var(--brand-gold-text);
  border: 1px solid rgba(198, 167, 94, 0.12);
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(198, 167, 94, 0.25);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0);
  }
`;

// ============================================================================
// AttachmentPreviewOverlay (Fix 2 — pre-send preview rail above threads band)
// ============================================================================

/**
 * Floating chip rail that lives between the ThreadSwitcherBar and the
 * composer input. Positioned absolute relative to ChatFooter so it
 * visually overlaps the bottom edge of the threads band; z-index is
 * one tick above the band but well below modals/dialogs.
 */
export const AttachmentPreviewOverlayContainer = styled.div`
  /* Inline strip rendered as a distinct row directly above the
     composer prompt bar. Sits visually "in front of" the threads
     band (when present) by using a higher z-index than the band
     and a solid background, but it stays in normal document flow
     so it can't be hidden behind ancestor overflow:hidden. */
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
  padding: 10px 14px;
  min-height: 72px;
  overflow-x: auto;
  overflow-y: visible;
  background: var(--brand-canvas);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.04);
  z-index: 6;
  
  /* Hide the physical scrollbar track so it stays clean, but
     user can still swipe/scroll it naturally */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 600px) {
    gap: 8px;
    padding: 8px 10px;
    min-height: 64px;
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    gap: 8px;
    padding: 8px 10px;
    min-height: 64px;
  }
`;

const attachmentPreviewChipBase = `
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 6px 6px;
  min-width: 180px;
  max-width: 280px;
  background: #ffffff;
  border: 1px solid rgba(33, 150, 243, 0.25);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  @media (max-width: 600px) {
    min-width: 150px;
    max-width: 220px;
  }

  /* Narrow pane (desktop docked included) — mirrors the tier above. */
  @container chat-pane (max-width: 520px) {
    min-width: 150px;
    max-width: 220px;
  }
`;

export const AttachmentPreviewOverlayChip = styled.div`
  ${attachmentPreviewChipBase}
`;

/**
 * Placeholder chip for attachments persisted as draft metadata whose
 * blob has died (e.g. full page refresh). Click to re-attach.
 */
export const AttachmentPreviewPlaceholderChip = styled.button`
  ${attachmentPreviewChipBase}
  cursor: pointer;
  text-align: left;
  border-style: dashed;
  border-color: rgba(245, 158, 11, 0.45);
  background: rgba(254, 243, 199, 0.45);

  &:hover {
    border-color: rgba(245, 158, 11, 0.7);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.18);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(245, 158, 11, 0.65);
    outline-offset: 2px;
  }
`;

export const AttachmentPreviewThumbnail = styled.div<{ $placeholder?: boolean }>`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$placeholder ? 'rgba(33, 150, 243, 0.08)' : '#f1f5f9'};
  border: 1px solid rgba(0, 0, 0, 0.04);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const AttachmentPreviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
`;

export const AttachmentPreviewFilename = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AttachmentPreviewSize = styled.span`
  font-size: 0.68rem;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AttachmentPreviewRemoveButton = styled.button`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.15s ease, color 0.15s ease;

  svg {
    width: 12px;
    height: 12px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
  }

  &:hover {
    background: rgba(220, 38, 38, 0.12);
    color: #dc2626;
  }

  &:focus-visible {
    outline: 2px solid rgba(220, 38, 38, 0.5);
    outline-offset: 2px;
  }
`;

// ============================================================================
// InlineAttachmentMessage / Lightbox (Fix 3 — persistent inline previews on sent messages)
// ============================================================================

export const InlineAttachmentImage = styled.button`
  display: block;
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
  background: #f8fafc;
  cursor: zoom-in;
  /* Scale down inside narrow bubbles (~265px interior in the docked pane)
     instead of overflowing to the fixed 320px and getting clipped at the
     pane edge (docked-mode UX, 2026-07-13). */
  max-width: min(320px, 100%);
  max-height: 240px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  img {
    display: block;
    max-width: 100%;
    max-height: 240px;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(33, 150, 243, 0.18);
  }

  &:focus-visible {
    outline: 2px solid rgba(33, 150, 243, 0.6);
    outline-offset: 2px;
  }
`;

/**
 * Inline video / audio in the bubble (2026-09-08, Miles: a clip should preview and play in place
 * like a picture does, open larger, and download). The frame mirrors the image thumbnail's
 * footprint; the player is a real <video>/<audio> with native controls.
 */
export const InlineMediaFrame = styled.div`
  display: block;
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 12px;
  overflow: hidden;
  background: #0f172a;
  max-width: min(420px, 100%);

  video {
    display: block;
    width: 100%;
    max-height: 300px;
    background: #000;
  }

  audio {
    display: block;
    width: 100%;
    min-width: 240px;
    background: #f8fafc;
  }
`;

export const InlineMediaMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
  max-width: min(420px, 100%);
  font-size: 0.74rem;
  color: var(--brand-text-secondary, #475569);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  [data-inline-media-name] {
    flex: 1 1 10ch;
    min-width: 10ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--brand-text-primary, #1e293b);
    font-weight: 600;
  }

  [data-inline-media-size] {
    flex: none;
    opacity: 0.85;
  }
`;

export const InlineMediaActionButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(33, 150, 243, 0.35);
  background: rgba(33, 150, 243, 0.08);
  color: #1d4ed8;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    background: rgba(33, 150, 243, 0.16);
  }

  &:focus-visible {
    outline: 2px solid rgba(33, 150, 243, 0.6);
    outline-offset: 2px;
  }
`;

export const InlineAttachmentBrokenPlaceholder = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(254, 226, 226, 0.6);
  border: 1px dashed rgba(220, 38, 38, 0.35);
  color: #7f1d1d;
  font-size: 0.78rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

export const ImageLightboxBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.84);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Body-portaled: a hand-picked number one below the fullscreen takeover
     opened this lightbox invisibly behind it in expanded mode. */
  z-index: ${BODY_PORTAL_Z_INDEX};
  padding: 24px;
  cursor: zoom-out;
  animation: lightboxFadeIn 0.18s ease-out;

  @keyframes lightboxFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
    border-radius: 8px;
    cursor: default;
  }
`;

export const ImageLightboxCloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: #1e293b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
  }

  &:hover {
    background: #ffffff;
  }
`;

/**
 * T2 (thread-workflow UX) — "you are here" thread label in the chat header.
 * Sits in the HeaderSubtitle slot: always tells the user WHICH thread the
 * conversation pane is showing, in every view (floating/docked/fullscreen).
 * Renders as a quiet button; hover reveals the rename affordance.
 */
export const ThreadHeaderLabelButton = styled.button`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  /* GOLD IS A FILL, NEVER TEXT ON A LIGHT GROUND (theme.ts, 2026-09-12).
     This line painted --brand-gold-hover (#B6924C) at 0.72rem on the header's
     canvas-to-white gradient: 2.79:1 against a 4.5 requirement. It is the
     primary "which thread am I in" signal — and the ONLY one on a phone now
     that the chip strip is retired there. --brand-gold-text (#7a6125) is the
     token the rule names for this exact role, and measures 5.65:1 on the same
     ground. check-design-tokens.mjs cannot see this (both are real tokens,
     validly used) and axe never reaches a signed-in chat header. */
  color: var(--brand-gold-text);
  letter-spacing: 0.02em;
  /* No offsets (header identity block, Miles 2026-07-13): the label is line 2
     of HeaderTitleColumn's two-line column — the column owns the 2px line gap
     and both lines share the same left edge. The old margin-top + 12px
     padding-left indented this line against the persona name above it. */
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  /* Measure against the header title column (block + 100%), keeping the
     260px cap for wide panes. A hard 260px alone ellipsized far too late in
     the 420px docked pane, truncating INTO the action buttons — the label
     must shrink with the min-width:0 chain (docked-mode UX, 2026-07-13). */
  display: block;
  max-width: min(260px, 100%);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  /* Phones (2026-09-13): this is the thread SWITCHER here, not just a label,
     so it earns a real tap target and the room to say which thread you are in.
     Two lines rather than an ellipsis at 260px — a phone's thread titles are
     the same length as a desktop's and the pane is a third the width, so the
     cap truncated most of them; ChatTitle above it already wears exactly this
     two-line tier. */
  ${mediaMax.mobileViewport} {
    font-size: 0.8rem;
    max-width: 100%;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    padding: 4px 0;
  }

  /* Header rail tiers (2026-09-15): a pane narrower than the wide rail needs
     — the foldable in landscape, where the title read "Appointmen…" — gets
     the same two lines instead of the one-line 260px cap. Container-keyed on
     the SAME number as the rail rule (HEADER_RAIL_WIDE_MIN_PX); the desktop
     cap above is untouched for wide panes. Font size stays the desktop's:
     this is room, not a tap target. */
  @container chat-pane (max-width: ${HEADER_RAIL_WIDE_MIN_PX - 1}px) {
    max-width: 100%;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &:hover span[data-thread-header-title] {
    text-decoration: underline dotted rgba(122, 97, 37, 0.6);
    text-underline-offset: 2px;
  }

  &:focus-visible {
    /* Was accentGold at 0.6 alpha, which composites to roughly 1.6:1 on this
       header — an invisible focus ring on what is now the phone's thread
       switcher. WCAG 1.4.11 asks 3:1 of a non-text indicator. */
    outline: 2px solid var(--brand-gold-text);
    /* INSET ring: the label now lives inside HeaderTitleColumn's
       overflow:hidden (docked min-width:0 chain), so an OUTSIDE offset
       painted the ring into the clipped region and keyboard users lost
       it entirely (adversarial review, docked-UX round 2026-07-13). */
    outline-offset: -2px;
  }
`;

/** Inline rename input swapped in when the header thread label is clicked. */
export const ThreadHeaderRenameInput = styled.input`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  /* Same ground, same role as ThreadHeaderLabelButton above — this input
     REPLACES that label mid-interaction, so a different gold here would be a
     visible colour change on click as well as the same 2.8:1. */
  color: var(--brand-gold-text);
  letter-spacing: 0.02em;
  /* Same alignment contract as the label it swaps in for (header identity
     block, 2026-07-13): no margins — the column gap + shared left edge rule. */
  padding: 1px 6px;
  background: rgba(198, 167, 94, 0.06);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 4px;
  /* Respect the header title column in narrow panes (same shrink contract
     as the label it swaps in for — docked-mode UX, 2026-07-13). */
  width: min(200px, 100%);
  max-width: 100%;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: rgba(198, 167, 94, 0.6);
  }
`;

/**
 * T5a (thread-workflow UX) — transient archive confirmation. Appears above the
 * thread bar for ~2.5s after an archive so the chip vanishing is never the
 * only feedback. Non-interactive; announced politely to screen readers.
 */
export const ArchiveToastPill = styled.div`
  position: absolute;
  bottom: 96px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  max-width: 82%;
  padding: 7px 14px;
  background: rgba(26, 26, 26, 0.95);
  color: var(--brand-gold);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 999px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  animation: archiveToastIn 0.18s ease-out;

  @keyframes archiveToastIn {
    from { opacity: 0; transform: translateX(-50%) translateY(6px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/**
 * T4 (thread-workflow UX) — edge overflow indicator for the chip strip. The
 * strip's scrollbar is hidden, so without these, threads scrolled out of view
 * simply don't exist for the user. Shows the hidden-chip count per side; the
 * gold dot lights when a hidden thread has unread/processing activity.
 */
export const ThreadOverflowIndicator = styled.button<{ $hot?: boolean; $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => (props.$side === 'left' ? 'left: 2px;' : 'right: 46px;')}
  /* 46px = ThreadNewButton min-width (40) + rail gap (6): "sit just left of
     the +". When the mobile sheet trigger renders AFTER the + in the same
     rail, the + shifts ~46px left into that band — the pill painted on top
     of it and stole its taps (review 2026-08-14). :has() re-anchors the
     right pill past the trigger; the left pill is unaffected. */
  ${props => (props.$side === 'right' ? css`
    ${ThreadSwitcherActionRail}:has([data-thread-sheet-trigger]) & {
      right: 92px;
    }
  ` : '')}
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  background: rgba(26, 26, 26, 0.85);
  color: ${props => (props.$hot ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.65)')};
  border: 1px solid ${props => (props.$hot ? 'rgba(198, 167, 94, 0.45)' : 'rgba(198, 167, 94, 0.18)')};
  border-radius: 999px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    color: var(--brand-gold);
    border-color: rgba(198, 167, 94, 0.45);
  }

  &::after {
    content: '';
    display: ${props => (props.$hot ? 'inline-block' : 'none')};
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--brand-gold);
  }

  /* Touch: HIDDEN entirely (owner device screenshot 2026-08-14 — supersedes
     the earlier enlarge-to-34px pass). On a phone the strip fits ~1 wide
     chip, so these absolute overlays painted directly over the chip's title
     and its pin/× controls. The ☰ sheet trigger beside the strip is the
     mobile affordance for hidden threads — full list, unread badges, no
     occlusion. Desktop (incl. narrow hover-capable windows) keeps them. */
  @media (hover: none) {
    display: none;
  }
`;

/**
 * T3 (thread-workflow UX) — fullscreen thread sidebar. A Slack-like left rail
 * rendered ONLY in fullscreen view (>820px): full titles, relative activity
 * time, unread/processing badges, pin + archive affordances. Driven by the
 * SAME orderThreads as the chip strip, so views can never disagree.
 */
export const ThreadSidebarContainer = styled.nav`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 264px;
  display: flex;
  flex-direction: column;
  background: rgba(20, 20, 20, 0.98);
  border-right: 1px solid rgba(198, 167, 94, 0.18);
  z-index: 5;
  overflow: hidden;

  ${mediaMax.mobileViewport} {
    display: none;
  }
`;

export const ThreadSidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px 10px 16px;
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  /* Lowercase label (platform style, Miles 2026-07-13): no uppercase
     transform; tracking normalized to the 0.02em the other gold lowercase
     labels use (0.06em was tuned for caps). */
  letter-spacing: 0.02em;
`;

/**
 * Official brand wordmark in the sidebar's top-left (round 3, 2026-08-01):
 * "prest" + the 0-logo mark — the EXACT treatment the site Header /
 * LandingPage / GlobalFooter render (Space Grotesk 700, tight tracking,
 * inline logo image as the "0" at 1.1em, baseline-tucked), scaled to the
 * rail and recolored warm-white for the dark background. Replaces the plain
 * "threads" label; the sections below carry the "threads" wording now.
 */
export const SidebarBrandWordmark = styled.span`
  display: inline-flex;
  align-items: baseline;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: -0.03em;
  line-height: 1;
  color: #F3EFE6;
  white-space: nowrap;
  user-select: none;
`;

/** The 0-logo mark inside the wordmark — same technique as HeaderInlineLogo. */
export const SidebarBrandLogoImage = styled.img`
  display: inline-block;
  height: 1.1em;
  margin-left: -0.12em;
  margin-bottom: -0.15em;
  vertical-align: baseline;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(200, 167, 94, 0.15));
`;

export const ThreadSidebarList = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 8px 12px 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(198, 167, 94, 0.25) transparent;
`;

/**
 * Sidebar thread row. ACTIVE-thread treatment (owner directive, Miles
 * 2026-07-14 — mirror of ThreadChipButton): decisively filled gold-tinted
 * background + stronger gold border on the dark rail vs a whisper-subtle
 * outline on inactive rows. Fill/contrast/weight only — same geometry —
 * and distinct even while the uniform turn indicator spins in the row.
 */
export const ThreadSidebarRow = styled.button<{ $active?: boolean }>`
  /* Badge anchor (2026-09-08): the row is the containing block of its
     ThreadUnreadBadge. Without this the absolute badge resolved against the
     rail (overflow:hidden → clipped) or the fixed sheet (stacked on its
     corner) and never rendered — see the badge's doc. */
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  margin-bottom: 2px;
  background: ${props => props.$active ? 'rgba(198, 167, 94, 0.22)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(198, 167, 94, 0.65)' : 'rgba(198, 167, 94, 0.10)'};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    background: ${props => props.$active ? 'rgba(198, 167, 94, 0.26)' : 'rgba(198, 167, 94, 0.07)'};
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }

  &:hover [data-thread-pin-btn],
  &:focus-within [data-thread-pin-btn] {
    display: inline-flex;
  }
  [data-thread-pin-btn][data-pinned='true'] {
    display: inline-flex;
  }
  &:hover [data-thread-close-btn],
  &:focus-within [data-thread-close-btn] {
    display: inline-flex;
  }

  /* Touch (tablets >820px where the rail shows): no hover exists, so the
     pin/archive affordances are always visible. */
  @media (hover: none) {
    [data-thread-pin-btn],
    [data-thread-close-btn] {
      display: inline-flex;
    }
  }
`;

export const ThreadSidebarRowMain = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/**
 * Row title. Active: bolder + the family's brighter gold (#C6A75E) so it
 * pops on the active row's filled background; inactive stays the quieter
 * #B6924C at regular weight. Same font/size (Miles 2026-07-14).
 */
export const ThreadSidebarRowTitle = styled.span<{ $active?: boolean }>`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: ${props => props.$active ? 600 : 500};
  color: ${props => props.$active ? 'var(--brand-gold)' : 'var(--brand-gold-hover)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ThreadSidebarRowMeta = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  color: rgba(182, 146, 76, 0.55);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * "similar name" hint (2026-07-31): duplicate-titled threads are hard to tell
 * apart, and the removed "Choose a thread" panel's only real value was flagging
 * them. That signal now lives here — a passive pill on sidebar rows whose
 * normalized titles collide. Purely informational: no state, no dismissal.
 */
/** Flex line wrapping a sidebar row title so hints can sit beside it. */
export const ThreadSidebarRowTitleLine = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  & > ${'' /* title keeps its ellipsis inside the flex line */}span:first-child {
    min-width: 0;
  }
`;

export const ThreadSidebarSimilarNameHint = styled.span`
  flex: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 1px 5px;
  border-radius: 7px;
  color: rgba(198, 167, 94, 0.85);
  background: rgba(198, 167, 94, 0.14);
  border: 1px solid rgba(198, 167, 94, 0.35);
  white-space: nowrap;
`;

/**
 * Fullscreen exit + account menu (owner directive, Miles 2026-07-13): users
 * got stuck in fullscreen because the only way out was the small mode-cycle
 * icon top-right. The sidebar pins this footer to its bottom edge — account
 * menu button + a clearly labeled exit button in one consistent bottom-left
 * placement, in the rail's dark palette + gold accents. The footer never
 * scrolls with the list (list is the flex-1 scroller; the footer is
 * flex: none).
 */
export const ThreadSidebarFooter = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px 10px 10px;
  border-top: 1px solid rgba(198, 167, 94, 0.18);
`;

export const SidebarAccountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    background: rgba(198, 167, 94, 0.07);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
`;

/** Avatar circle: profile image when available, lowercase initials fallback. */
export const SidebarAccountAvatar = styled.span`
  flex: none;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(198, 167, 94, 0.16);
  border: 1px solid rgba(198, 167, 94, 0.35);
  color: var(--brand-gold);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const SidebarAccountName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--brand-gold-hover);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Labeled fullscreen exit. Calls the SAME mode handler as the header's
 * mode-cycle button (one source of truth for mode state — no sidebar-owned
 * mode); this is just a bigger, findable affordance for it.
 */
export const SidebarExitFullScreenButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--brand-gold-hover);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  svg {
    flex: none;
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.07);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 1px;
  }
`;

/**
 * Account popover — body-portaled at BODY_PORTAL_Z_INDEX (fullscreen burial
 * lesson, see the constant's doc: anything portaled below the fullscreen
 * chat's z-index opens invisibly BEHIND it). Fixed coordinates come through
 * attrs->style, same pattern as ThreadChipTooltip. Opens upward from the
 * bottom-left footer.
 */
export const SidebarAccountMenu = styled.div.attrs<{ $left: number; $bottom: number }>((props) => ({
  style: {
    left: `${props.$left}px`,
    bottom: `${props.$bottom}px`,
  },
}))<{ $left: number; $bottom: number }>`
  position: fixed;
  z-index: ${BODY_PORTAL_Z_INDEX};
  min-width: 208px;
  max-width: min(280px, calc(100vw - 16px));
  padding: 6px;
  background: rgba(26, 26, 26, 0.98);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(198, 167, 94, 0.12);
`;

export const SidebarAccountMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--brand-gold);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  cursor: pointer;
  text-align: left;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    background: rgba(198, 167, 94, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: -2px;
  }
`;

/* ── F4-A empty-thread starter chips ────────────────────────────────────────
 * One-click starter prompts rendered under the welcome bubble on an empty
 * thread. Replaces the old copy-paste-only `<li>` bullets. Left-aligned to sit
 * under the assistant welcome bubble; wraps on narrow surfaces. */
export const StarterChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 6px 0;
  padding: 0 4px;
`;

export const StarterChip = styled.button`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 8px 14px;
  border-radius: 18px;
  border: 1px solid rgba(198, 167, 94, 0.4);
  background: #FBF7EE;
  color: #8a6d2f;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0.01em;
  cursor: pointer;
  white-space: normal;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: #F5EFE0;
    border-color: rgba(198, 167, 94, 0.7);
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  /* Narrow pane (docked round 2, 2026-08-02): the chips already WRAP (no
     horizontal crowding), but four multi-line prompts towered over the
     composer in a ≤480px pane — one padding/font step keeps the stack
     shorter without touching wide modes. */
  @container chat-pane (max-width: 520px) {
    padding: 7px 12px;
    font-size: 12.5px;
  }
`;

/* ── Returning-tab reliability (2026-09-01): hydrate skeletons + retry ──────
 * The fullscreen rail and the transcript pane both used to render NOTHING
 * while their stores hydrated (blank rail until threadStore.ready; blank
 * pane on an empty-local thread until the server read landed) — a returning
 * tab read as "my threads are gone". These exports live OUTSIDE the
 * ChatMessages template block on purpose: threadScrollingUx's describe-2
 * pins slice styles.ts from 'export const ChatMessages' to the next
 * 'export const' and assert exact strings inside that slice. */

export const skeletonShimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

/** Rail placeholder — same geometry/palette as ThreadSidebarContainer so the
 *  layout doesn't shift when the real rail replaces it. */
export const ThreadSidebarSkeleton = styled.nav`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 264px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 14px;
  background: rgba(20, 20, 20, 0.98);
  border-right: 1px solid rgba(198, 167, 94, 0.18);
  z-index: 5;
  overflow: hidden;

  ${mediaMax.mobileViewport} {
    display: none;
  }
`;

export const SidebarSkeletonLabel = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--brand-gold-hover);
  padding: 0 2px 4px;
`;

export const SidebarSkeletonRow = styled.div`
  height: 38px;
  border-radius: 8px;
  border: 1px solid rgba(198, 167, 94, 0.10);
  background: linear-gradient(
    90deg,
    rgba(198, 167, 94, 0.06) 25%,
    rgba(198, 167, 94, 0.14) 50%,
    rgba(198, 167, 94, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: ${skeletonShimmer} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Transcript hydrate placeholder — three bubble-shaped shimmer rows. */
export const TranscriptSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 4px;
`;

export const TranscriptSkeletonBubble = styled.div<{ $user?: boolean }>`
  height: 44px;
  width: ${props => props.$user ? '46%' : '68%'};
  align-self: ${props => props.$user ? 'flex-end' : 'flex-start'};
  border-radius: 14px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.045) 25%,
    rgba(0, 0, 0, 0.09) 50%,
    rgba(0, 0, 0, 0.045) 75%
  );
  background-size: 200% 100%;
  animation: ${skeletonShimmer} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const TranscriptSkeletonNotice = styled.span`
  align-self: center;
  padding-top: 2px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #94a3b8;
`;

/** Failed-hydrate retry row — inline in the transcript, never a banner band. */
export const TranscriptRetryRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  color: #64748b;
`;

export const TranscriptRetryButton = styled.button`
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 5px 14px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #64748b;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease-out;

  &:hover {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.6);
    outline-offset: 2px;
  }
`;
